import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { restaurantAPI, messagesAPI } from '../services/api';
import FAQFeedback from '../components/FAQFeedback';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
/** Resolve image URLs — prepend backend origin for relative /static/ paths */
const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

const UK_ALLERGENS = [
  'celery','gluten','crustaceans','eggs','fish','lupin',
  'milk','molluscs','mustard','nuts','peanuts','sesame','soya','sulphites'
];

const ORDER_STATUS_FLOW = {
  placed:           { next: 'accepted',         label: 'Accept Order',                actionColor: 'bg-green-500 hover:bg-green-600', color: 'bg-blue-100 text-blue-800', icon: '📋', ring: 'ring-blue-400' },
  accepted:         { next: 'preparing',         label: 'Start Preparing',            actionColor: 'bg-yellow-500 hover:bg-yellow-600', color: 'bg-yellow-100 text-yellow-800', icon: '✅', ring: 'ring-yellow-400' },
  preparing:        { next: 'ready_for_pickup',  label: 'Mark Ready & Assign Rider',  actionColor: 'bg-purple-600 hover:bg-purple-700', color: 'bg-orange-100 text-orange-800', icon: '👨‍🍳', ring: 'ring-orange-400' },
  ready_for_pickup: { next: null,                label: 'Awaiting Rider',             color: 'bg-purple-100 text-purple-800', icon: '📦', ring: 'ring-purple-400' },
  out_for_delivery: { next: null,                label: 'Out for Delivery',           color: 'bg-indigo-100 text-indigo-800', icon: '🛵', ring: 'ring-indigo-400' },
  delivered:        { next: null,                label: 'Delivered',                  color: 'bg-green-100 text-green-800', icon: '✅' },
  rejected:         { next: null,                label: 'Rejected',                   color: 'bg-red-100 text-red-800', icon: '❌' },
  cancelled:        { next: null,                label: 'Cancelled',                  color: 'bg-gray-100 text-gray-800', icon: '❌' },
};

// Self-delivery restaurants can advance orders themselves (no rider assignment)
const SELF_DELIVERY_STATUS_FLOW = {
  ...ORDER_STATUS_FLOW,
  preparing:        { next: 'ready_for_pickup',  label: 'Mark Ready',                 actionColor: 'bg-purple-600 hover:bg-purple-700', color: 'bg-orange-100 text-orange-800', icon: '👨‍🍳', ring: 'ring-orange-400' },
  ready_for_pickup: { next: 'out_for_delivery',  label: 'Send Out for Delivery',      actionColor: 'bg-indigo-600 hover:bg-indigo-700', color: 'bg-purple-100 text-purple-800', icon: '📦', ring: 'ring-purple-400' },
  out_for_delivery: { next: 'delivered',          label: 'Mark Delivered',             actionColor: 'bg-green-600 hover:bg-green-700', color: 'bg-indigo-100 text-indigo-800', icon: '🛵', ring: 'ring-indigo-400' },
};

const REJECT_NEXT = {
  placed: 'rejected',
  accepted: 'cancelled',
  preparing: 'cancelled',
};

const MENU_CATEGORIES = [
  'Starters', 'Mains', 'Sides', 'Desserts', 'Drinks', 'Salads',
  'Specials', 'Kids Menu', 'Breakfast', 'Lunch', 'Dinner', 'Other'
];

export default function RestaurantDashboard({ token, user, onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [profile, setProfile] = useState(null);
  // Launch-phase rollout status — how many verified riders are in the
  // restaurant's city vs the threshold needed to flip them live. Only
  // shown when the restaurant is in coming_soon state.
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orderFilter, setOrderFilter] = useState('active'); // active | history | all
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [menuCategoryFilter, setMenuCategoryFilter] = useState('');
  const [menuSearchQuery, setMenuSearchQuery] = useState('');

  // Menu form
  const [showMenuForm, setShowMenuForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [menuForm, setMenuForm] = useState({
    name: '', category: '', description: '', price: '',
    isAvailable: true, imageUrl: '', calories: '', prepTime: '',
    allergens: [], containsNoAllergens: false, isVegan: false, isVegetarian: false,
    isGlutenFree: false, isHalal: false, spiceLevel: 0
  });
  const [imageUploading, setImageUploading] = useState(false);
  const imageInputRef = useRef(null);
  const [menuPerformance, setMenuPerformance] = useState(null);

  // Messages state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const messagesPollRef = useRef(null);

  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: '', discountType: 'percentage', discountValue: '', minOrderValue: '',
    maxUses: '', perUserLimit: 1, validUntil: '', firstOrderOnly: false, description: ''
  });

  // Payout state
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [payoutPeriod, setPayoutPeriod] = useState('week');

  // Review response state
  const [reviewResponseText, setReviewResponseText] = useState('');
  const [respondingToOrder, setRespondingToOrder] = useState(null);

  // Settings state — notification preferences (loaded from backend)
  const [notifPrefs, setNotifPrefs] = useState(null);
  const notifPrefsLoadedRef = useRef(false);
  const [showBusyConfirm, setShowBusyConfirm] = useState(false);
  const [showClosureConfirm, setShowClosureConfirm] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [busyUntil, setBusyUntil] = useState(null);
  const [snoozeMinutes, setSnoozeMinutes] = useState(null); // null=indefinite, number=timed
  const [showGuideModal, setShowGuideModal] = useState(false);

  // Hybrid Fulfillment Toggle & Delivery Schedule
  const [deliveryScheduleData, setDeliveryScheduleData] = useState(null);
  const [deliveryScheduleEditing, setDeliveryScheduleEditing] = useState(false);
  const [deliveryScheduleForm, setDeliveryScheduleForm] = useState({});

  // Driver Management (Self-Delivery)
  const [staffDrivers, setStaffDrivers] = useState([]);
  const [showAddDriverModal, setShowAddDriverModal] = useState(false);
  const [addDriverForm, setAddDriverForm] = useState({ name: '', email: '' });

  // Pickup Code Modal (Self-Delivery)
  const [showPickupCodeModal, setShowPickupCodeModal] = useState(false);
  const [pickupCodeInput, setPickupCodeInput] = useState('');
  const [pickupCodeOrderId, setPickupCodeOrderId] = useState(null);
  const [pickupCodeError, setPickupCodeError] = useState('');

  // Order action modals
  const [prepTimeInput, setPrepTimeInput] = useState('');
  const [rejectionReasonInput, setRejectionReasonInput] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(null);

  // Profile form
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({});

  // Operating schedule editing
  const [scheduleEditing, setScheduleEditing] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({});
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // Order settings editing
  const [orderSettingsEditing, setOrderSettingsEditing] = useState(false);
  const [orderSettingsForm, setOrderSettingsForm] = useState({
    minimumOrderValue: '', estimatedDeliveryTime: '', deliveryRadius: '',
    acceptsDelivery: true, acceptsCollection: true, defaultPrepTime: ''
  });
  const [orderSettingsSaving, setOrderSettingsSaving] = useState(false);

  // Payout details editing
  const [payoutEditing, setPayoutEditing] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    bankAccountName: '', bankSortCode: '', bankAccountNumber: ''
  });
  const [payoutSaving, setPayoutSaving] = useState(false);

  // Sound notification for new orders
  const lastOrderCountRef = useRef(0);

  useEffect(() => {
    if (!token) return; // wait for auth to be ready
    loadDashboard();
    loadProfile(); // pre-load profile for Settings tab
    restaurantAPI.getCoverageStatus(token).then(setCoverage).catch(() => {});
    const poll = setInterval(() => { loadOrders(); }, 15000); // faster poll — 15s
    // Refresh coverage every 60s so a newly approved rider in the area is
    // reflected without the restaurant owner having to reload.
    const coveragePoll = setInterval(() => {
      restaurantAPI.getCoverageStatus(token).then(setCoverage).catch(() => {});
    }, 60000);
    return () => { clearInterval(poll); clearInterval(coveragePoll); };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    if (tab === 'orders') loadOrders();
    if (tab === 'menu') loadMenu();
    if (tab === 'settings') {
      loadProfile();
      // Load delivery schedule and staff
      restaurantAPI.getDeliverySchedule(token).then(setDeliveryScheduleData).catch(() => {});
      if (stats.fulfillmentMode === 'self_delivery') {
        restaurantAPI.getStaff(token).then(d => setStaffDrivers(d.staff || [])).catch(() => {});
      }
    }
    if (tab === 'analytics') { loadMenu(); loadMenuPerformance(); loadPayoutSummary(); }
    if (tab === 'messages') loadConversations();
    if (tab === 'promos') loadPromoCodes();
  }, [tab, token]);

  // Alert sound for new orders
  useEffect(() => {
    const newCount = orders.filter(o => o.status === 'placed').length;
    if (newCount > lastOrderCountRef.current && lastOrderCountRef.current !== 0) {
      try { new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdGeBi4iFdmhpfIaLh4R5bnR+hIiHg3x0d36Cg4OBfnt7f4GCgYB+fX5/gIGBgH9+fn+AgYGAf39+f4CBgYCAf39/gIGBgIB/f3+AgYGAgH9/f4CBgYCAf39/gIGBgIB/f3+AgYGAgH9/f4CBgQ==').play(); } catch (e) {}
    }
    lastOrderCountRef.current = newCount;
  }, [orders]);

  const loadDashboard = async () => {
    try {
      const data = await restaurantAPI.getDashboard(token);
      setDashboard(data);
      setOrders(data.recentOrders || []);
    } catch (e) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const data = await restaurantAPI.getOrders(token);
      setOrders(data);
    } catch (e) { console.error('Failed to load orders:', e); }
  };

  const loadMenu = async () => {
    try {
      const data = await restaurantAPI.getMenuItems(token);
      setMenuItems(data);
    } catch (e) { console.error('Failed to load menu items:', e); }
  };

  const loadProfile = async () => {
    try {
      const data = await restaurantAPI.getProfile(token);
      setProfile(data);
      setProfileForm({
        restaurantName: data.restaurantName || '',
        phone: data.phone || '',
        city: data.city || '',
        address: data.address || '',
        postcode: data.postcode || '',
        openingHours: data.openingHours || '',
        description: data.description || '',
        minimumOrderValue: data.minimumOrderValue || '',
        estimatedDeliveryTime: data.estimatedDeliveryTime || '',
        deliveryRadius: data.deliveryRadius || '',
        cuisineTypes: (data.cuisineTypes || []).join(', '),
      });
      // Sync busy/snooze state from backend
      setIsBusy(data.isBusy || false);
      setBusyUntil(data.busyUntil || null);

      // Load notification preferences from backend
      if (!notifPrefsLoadedRef.current) {
        try {
          const prefs = await restaurantAPI.getNotificationPreferences(token);
          setNotifPrefs(prefs);
          notifPrefsLoadedRef.current = true;
        } catch (e) {
          // Fallback defaults if endpoint fails
          setNotifPrefs({
            newOrderAlerts: true,
            orderStatusUpdates: true,
            dailySalesSummary: false,
            customerReviews: true,
            lowStockAlerts: false,
          });
          notifPrefsLoadedRef.current = true;
        }
      }
    } catch (e) { console.error('Failed to load profile:', e); }
  };

  const loadMenuPerformance = async () => {
    try {
      const data = await restaurantAPI.getMenuPerformance(token);
      setMenuPerformance(data);
    } catch (e) { console.error('Failed to load menu performance:', e); }
  };

  const loadPromoCodes = async () => {
    try {
      const data = await restaurantAPI.getPromoCodes(token);
      setPromoCodes(data);
    } catch (e) { console.error('Failed to load promo codes:', e); }
  };

  const loadPayoutSummary = async (period = payoutPeriod) => {
    try {
      const data = await restaurantAPI.getPayoutSummary(period, token);
      setPayoutSummary(data);
    } catch (e) { console.error('Failed to load payout summary:', e); }
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await restaurantAPI.createPromoCode({
        code: promoForm.code,
        discountType: promoForm.discountType,
        discountValue: parseFloat(promoForm.discountValue),
        minOrderValue: promoForm.minOrderValue ? parseFloat(promoForm.minOrderValue) : 0,
        maxUses: promoForm.maxUses ? parseInt(promoForm.maxUses) : null,
        perUserLimit: parseInt(promoForm.perUserLimit) || 1,
        validUntil: promoForm.validUntil || null,
        firstOrderOnly: promoForm.firstOrderOnly,
        description: promoForm.description,
      }, token);
      setSuccess('Promo code created!');
      setShowPromoForm(false);
      setPromoForm({ code: '', discountType: 'percentage', discountValue: '', minOrderValue: '', maxUses: '', perUserLimit: 1, validUntil: '', firstOrderOnly: false, description: '' });
      loadPromoCodes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleToggleItemAvailability = async (itemId, currentStatus) => {
    try {
      await restaurantAPI.toggleItemAvailability(itemId, !currentStatus, token);
      loadMenu();
    } catch (e) { console.error('Failed to toggle availability:', e); }
  };

  const handleReviewResponse = async (orderId) => {
    if (!reviewResponseText.trim()) return;
    try {
      await restaurantAPI.respondToReview(orderId, reviewResponseText.trim(), token);
      setRespondingToOrder(null);
      setReviewResponseText('');
      setSuccess('Response saved!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save response');
    }
  };

  const handleAcceptWithPrepTime = async (orderId) => {
    const prepTime = parseInt(prepTimeInput) || 25;
    // Capture order data BEFORE clearing modal (for auto-print)
    const orderToPrint = orders.find(o => o._id === orderId);
    setShowAcceptModal(null);
    setPrepTimeInput('');
    try {
      await restaurantAPI.updateOrderStatus(orderId, 'accepted', token, { estimatedPrepTime: prepTime });
      await loadOrders();
      setSuccess(`Order accepted! Prep time: ${prepTime} mins`);
      setTimeout(() => setSuccess(''), 5000);
      // Auto-print receipt with pickup code on accept
      if (orderToPrint) {
        printOrderReceipt(orderToPrint);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not accept order');
    }
  };

  const handleRejectWithReason = async (orderId) => {
    setShowRejectModal(null);
    try {
      await restaurantAPI.updateOrderStatus(orderId, 'rejected', token, { rejectionReason: rejectionReasonInput || 'No reason provided' });
      setRejectionReasonInput('');
      await loadOrders();
      setSuccess('Order rejected.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not reject order');
    }
  };

  // ── Messages functions ──
  const loadConversations = async () => {
    try {
      const data = await messagesAPI.getConversations(token, 'host');
      // Filter to restaurant-type conversations only
      const restaurantConvs = data.filter(c =>
        c.conversationType === 'restaurant' ||
        c.conversationType === 'delivery_pickup' ||
        c.relatedType === 'food_order'
      );
      setConversations(restaurantConvs);
      // Count unread
      const unread = restaurantConvs.reduce((sum, c) => sum + (c.unreadHost || 0), 0);
      setUnreadCount(unread);
    } catch (e) { console.error('Failed to load conversations:', e); }
  };

  const loadMessages = async (conversationId) => {
    try {
      const data = await messagesAPI.getConversation(conversationId, token);
      setMessages(data.messages || []);
      // Mark as read
      await messagesAPI.markConversationRead(conversationId, token);
      // Update local unread count
      setConversations(prev => prev.map(c =>
        c._id === conversationId ? { ...c, unreadHost: 0 } : c
      ));
      setUnreadCount(prev => Math.max(0, prev - (data.conversation?.unreadHost || 0)));
      // Scroll to bottom
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) { console.error('Failed to load messages:', e); }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;
    setSendingMessage(true);
    try {
      await messagesAPI.sendMessage({
        conversationId: selectedConversation._id,
        content: newMessage.trim()
      }, token);
      setNewMessage('');
      await loadMessages(selectedConversation._id);
    } catch (e) {
      console.error('Failed to send message:', e);
    } finally {
      setSendingMessage(false);
    }
  };

  // Poll for new messages when on messages tab
  useEffect(() => {
    if (tab === 'messages' && token) {
      loadConversations();
      messagesPollRef.current = setInterval(loadConversations, 10000);
      return () => clearInterval(messagesPollRef.current);
    }
    return () => { if (messagesPollRef.current) clearInterval(messagesPollRef.current); };
  }, [tab, token]);

  // Poll messages in selected conversation
  useEffect(() => {
    if (selectedConversation && tab === 'messages') {
      loadMessages(selectedConversation._id);
      const poll = setInterval(() => loadMessages(selectedConversation._id), 8000);
      return () => clearInterval(poll);
    }
  }, [selectedConversation?._id, tab]);

  // Load unread count on mount for badge
  useEffect(() => {
    if (!token) return;
    messagesAPI.getConversations(token, 'host').then(data => {
      const restaurantConvs = data.filter(c =>
        c.conversationType === 'restaurant' || c.conversationType === 'delivery_pickup' || c.relatedType === 'food_order'
      );
      const unread = restaurantConvs.reduce((sum, c) => sum + (c.unreadHost || 0), 0);
      setUnreadCount(unread);
    }).catch(() => {});
  }, [token]);

  // =========================================================================
  //  THERMAL RECEIPT PRINTING — ESC/POS compatible
  //  Opens a print-optimised window with the order + 6-digit pickup code.
  //  Works with:
  //    - USB/LAN thermal printers (via browser print dialog)
  //    - Bluetooth thermal printers (via Web Bluetooth or helper app)
  //    - Star, Epson, and generic ESC/POS 80mm/58mm receipt printers
  //  The receipt uses a 58mm/80mm-width layout with monospaced fonts,
  //  the pickup code in a huge bold font, and a clean item list.
  // =========================================================================
  const printOrderReceipt = (order) => {
    const pickupCode = order.pickupCode || '------';
    const formattedCode = `${pickupCode.slice(0,3)}-${pickupCode.slice(3)}`;
    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const dateStr = now.toLocaleDateString('en-GB');

    const itemsHtml = (order.items || []).map(item =>
      `<tr>
        <td style="text-align:left;padding:2px 0;">${item.quantity}x ${item.name}</td>
        <td style="text-align:right;padding:2px 0;white-space:nowrap;">${(item.price * item.quantity).toFixed(2)}</td>
      </tr>`
    ).join('');

    const receiptHtml = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Order ${order.orderNumber}</title>
<style>
  @page { size: 80mm auto; margin: 2mm; }
  @media print { body { width: 76mm; } .no-print { display: none !important; } }
  body { font-family: 'Courier New', Courier, monospace; font-size: 12px; width: 76mm; margin: 0 auto; padding: 4mm; color: #000; }
  .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 6px; margin-bottom: 6px; }
  .header h1 { font-size: 16px; margin: 0; font-weight: bold; }
  .header p { font-size: 10px; margin: 2px 0; }
  .pickup-code-box { text-align: center; border: 3px solid #000; border-radius: 8px; padding: 8px 4px; margin: 8px 0; background: #f5f5f5; }
  .pickup-code-label { font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 4px; }
  .pickup-code { font-size: 36px; font-weight: 900; letter-spacing: 6px; font-family: 'Courier New', monospace; }
  .pickup-note { font-size: 9px; margin-top: 4px; color: #444; }
  .order-info { border-bottom: 1px dashed #000; padding: 4px 0; margin-bottom: 4px; }
  .order-info p { margin: 2px 0; font-size: 11px; }
  .items-table { width: 100%; border-collapse: collapse; font-size: 11px; }
  .items-table td { padding: 2px 0; }
  .total-row { border-top: 2px solid #000; font-weight: bold; font-size: 14px; padding-top: 4px; margin-top: 4px; }
  .footer { text-align: center; border-top: 2px dashed #000; padding-top: 6px; margin-top: 8px; font-size: 9px; color: #666; }
  .delivery-badge { display: inline-block; padding: 2px 8px; border: 1px solid #000; border-radius: 4px; font-size: 10px; font-weight: bold; margin-top: 4px; }
  .cut-line { text-align: center; font-size: 8px; color: #ccc; margin: 8px 0; letter-spacing: 2px; }
  .print-btn { display: block; width: 100%; padding: 12px; font-size: 16px; font-weight: bold; background: #d97706; color: white; border: none; border-radius: 8px; cursor: pointer; margin: 10px 0; }
  .print-btn:hover { background: #b45309; }
</style></head><body>
  <div class="no-print" style="text-align:center;margin-bottom:12px;">
    <button class="print-btn" onclick="window.print()">🖨️ Print Receipt</button>
    <p style="font-size:10px;color:#888;">Connect your thermal printer (USB/Bluetooth/LAN) and click Print</p>
  </div>

  <div class="header">
    <h1>Ariv</h1>
    <p>${order.restaurantName || stats.restaurantName || 'Restaurant'}</p>
    <p>${dateStr} ${timeStr}</p>
  </div>

  <div class="pickup-code-box">
    <div class="pickup-code-label">PICKUP CODE</div>
    <div class="pickup-code">${formattedCode}</div>
    <div class="pickup-note">STAPLE TO BAG SEAL — RIDER MUST ENTER THIS CODE</div>
  </div>

  <div class="order-info">
    <p><strong>ORDER:</strong> #${order.orderNumber}</p>
    <p><strong>GUEST:</strong> ${order.guestName || 'Guest'}</p>
    <p><strong>TYPE:</strong> ${order.deliveryType === 'collection' ? 'COLLECTION' : 'DELIVERY'}</p>
    ${order.deliveryAddress && order.deliveryType !== 'collection' ? `<p><strong>ADDRESS:</strong> ${order.deliveryAddress}</p>` : ''}
    ${order.notes ? `<p><strong>NOTES:</strong> ${order.notes}</p>` : ''}
    <span class="delivery-badge">${order.fulfillmentMode === 'self_delivery' ? 'SELF-DELIVERY' : 'PLATFORM DELIVERY'}</span>
  </div>

  <table class="items-table">
    ${itemsHtml}
  </table>

  <div class="total-row" style="display:flex;justify-content:space-between;">
    <span>TOTAL</span>
    <span>£${(order.total || 0).toFixed(2)}</span>
  </div>

  ${order.deliveryPin ? `
  <div style="text-align:center;margin-top:6px;padding:4px;border:1px dashed #000;border-radius:4px;">
    <p style="font-size:9px;font-weight:bold;margin:0;">DELIVERY PIN (GUEST)</p>
    <p style="font-size:20px;font-weight:900;margin:2px 0;letter-spacing:4px;">${order.deliveryPin}</p>
  </div>` : ''}

  <div class="footer">
    <p>Powered by Ariv</p>
    <p>ariv.com</p>
  </div>

  <div class="cut-line">- - - - - - - - - CUT HERE - - - - - - - - -</div>

  <script>
    // Auto-print when opened (for silent printing workflows)
    if (window.matchMedia) {
      // Only auto-print if print media is supported
      setTimeout(() => { window.print(); }, 500);
    }
  </script>
</body></html>`;

    const printWindow = window.open('', '_blank', 'width=350,height=600');
    if (printWindow) {
      printWindow.document.write(receiptHtml);
      printWindow.document.close();
    }
  };

  const handleOrderAction = async (orderId, newStatus) => {
    setActionLoading(prev => ({ ...prev, [orderId]: true }));
    setError('');
    try {
      await restaurantAPI.updateOrderStatus(orderId, newStatus, token);
      await loadOrders();
      if (newStatus === 'accepted') {
        setSuccess('Order accepted! Customer has been notified.');
      } else if (newStatus === 'preparing') {
        setSuccess('Kitchen started preparing the order.');
      } else if (newStatus === 'ready_for_pickup') {
        setSuccess('Order marked ready! A delivery rider will be assigned automatically.');
      }
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update order status');
    } finally {
      setActionLoading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const openMenuForm = (item = null) => {
    if (item) {
      setEditingItem(item);
      setMenuForm({
        name: item.name || '',
        category: item.category || '',
        description: item.description || '',
        price: item.price || '',
        isAvailable: item.isAvailable !== undefined ? item.isAvailable : true,
        imageUrl: item.imageUrl || '',
        calories: item.calories || '',
        prepTime: item.prepTime || '',
        allergens: item.allergens || [],
        containsNoAllergens: item.containsNoAllergens || false,
        isVegan: item.isVegan || false,
        isVegetarian: item.isVegetarian || false,
        isGlutenFree: item.isGlutenFree || false,
        isHalal: item.isHalal || false,
        spiceLevel: item.spiceLevel || 0,
      });
    } else {
      setEditingItem(null);
      setMenuForm({
        name: '', category: '', description: '', price: '',
        isAvailable: true, imageUrl: '', calories: '', prepTime: '',
        allergens: [], containsNoAllergens: false, isVegan: false, isVegetarian: false,
        isGlutenFree: false, isHalal: false, spiceLevel: 0
      });
    }
    setShowMenuForm(true);
  };

  // Image upload handler
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large. Maximum 5MB.');
      return;
    }
    setImageUploading(true);
    try {
      const result = await restaurantAPI.uploadImage(file, token);
      setMenuForm(prev => ({ ...prev, imageUrl: result.imageUrl }));
      setSuccess('Image uploaded');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      setError('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleMenuSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Allergen declaration is mandatory: either list allergens or positively
    // confirm the item contains none of the UK 14 (backend enforces too).
    if (menuForm.allergens.length === 0 && !menuForm.containsNoAllergens) {
      setError('Allergen declaration required: select the allergens this dish contains, or tick "contains none of the 14 major allergens".');
      return;
    }
    const payload = {
      ...menuForm,
      price: parseFloat(menuForm.price),
      calories: menuForm.calories ? parseInt(menuForm.calories) : null,
      prepTime: menuForm.prepTime ? parseInt(menuForm.prepTime) : null,
      spiceLevel: parseInt(menuForm.spiceLevel),
    };
    try {
      if (editingItem) {
        await restaurantAPI.updateMenuItem(editingItem._id || editingItem.id, payload, token);
      } else {
        await restaurantAPI.createMenuItem(payload, token);
      }
      setShowMenuForm(false);
      await loadMenu();
      setSuccess(editingItem ? 'Menu item updated' : 'Menu item added');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save menu item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this menu item? This cannot be undone.')) return;
    try {
      await restaurantAPI.deleteMenuItem(itemId, token);
      await loadMenu();
      setSuccess('Menu item deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Could not delete item');
    }
  };

  const toggleItemAvailability = async (item) => {
    try {
      await restaurantAPI.updateMenuItem(item._id || item.id, { isAvailable: !item.isAvailable }, token);
      await loadMenu();
    } catch (e) {
      setError('Could not update availability');
    }
  };

  const handleProfileSave = async () => {
    try {
      const data = { ...profileForm };
      if (data.cuisineTypes && typeof data.cuisineTypes === 'string') {
        data.cuisineTypes = data.cuisineTypes.split(',').map(s => s.trim()).filter(Boolean);
      }
      if (data.minimumOrderValue) data.minimumOrderValue = parseFloat(data.minimumOrderValue);
      if (data.deliveryRadius) data.deliveryRadius = parseFloat(data.deliveryRadius);
      await restaurantAPI.updateProfile(data, token);
      setProfileEditing(false);
      setSuccess('Profile updated');
      setTimeout(() => setSuccess(''), 3000);
      await loadProfile();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save profile');
    }
  };

  // Generate time options in 30-minute intervals (00:00 to 23:30)
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      timeOptions.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }

  const handleScheduleEdit = () => {
    const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
    const initial = {};
    days.forEach(day => {
      const existing = profile?.operatingHours?.[day];
      if (existing && existing.open) {
        initial[day] = {
          open: true,
          openTime: existing.openTime || '11:00',
          closeTime: existing.closeTime || '23:00'
        };
      } else {
        initial[day] = { open: false, openTime: '11:00', closeTime: '23:00' };
      }
    });
    setScheduleForm(initial);
    setScheduleEditing(true);
  };

  const handleScheduleSave = async () => {
    try {
      setScheduleSaving(true);
      await restaurantAPI.updateProfile({ operatingHours: scheduleForm }, token);
      setScheduleEditing(false);
      setSuccess('Operating schedule updated');
      setTimeout(() => setSuccess(''), 3000);
      await loadProfile();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save schedule');
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleOrderSettingsEdit = () => {
    setOrderSettingsForm({
      minimumOrderValue: profile?.minimumOrderValue || '',
      estimatedDeliveryTime: profile?.estimatedDeliveryTime || '30-45 mins',
      deliveryRadius: profile?.deliveryRadius || 5,
      acceptsDelivery: profile?.acceptsDelivery !== false,
      acceptsCollection: profile?.acceptsCollection !== false,
      defaultPrepTime: profile?.defaultPrepTime || '',
    });
    setOrderSettingsEditing(true);
  };

  const handleOrderSettingsSave = async () => {
    try {
      setOrderSettingsSaving(true);
      const data = {};
      if (orderSettingsForm.minimumOrderValue !== '') data.minimumOrderValue = parseFloat(orderSettingsForm.minimumOrderValue);
      if (orderSettingsForm.estimatedDeliveryTime) data.estimatedDeliveryTime = orderSettingsForm.estimatedDeliveryTime;
      if (orderSettingsForm.deliveryRadius !== '') data.deliveryRadius = parseFloat(orderSettingsForm.deliveryRadius);
      if (orderSettingsForm.defaultPrepTime !== '') data.defaultPrepTime = parseInt(orderSettingsForm.defaultPrepTime, 10);
      data.acceptsDelivery = orderSettingsForm.acceptsDelivery;
      data.acceptsCollection = orderSettingsForm.acceptsCollection;
      await restaurantAPI.updateProfile(data, token);
      setOrderSettingsEditing(false);
      setSuccess('Order settings updated');
      setTimeout(() => setSuccess(''), 3000);
      await loadProfile();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save order settings');
    } finally {
      setOrderSettingsSaving(false);
    }
  };

  const handlePayoutEdit = () => {
    // Bank fields start EMPTY on edit. The server returns masked values
    // (****1234 / **-**-56) and round-tripping those as new values would
    // either corrupt the record or fail the 6/8-digit validation. Empty
    // input means "leave unchanged" and we drop the field from the PATCH.
    setPayoutForm({
      bankAccountName: profile?.bankDetails?.accountName || '',
      bankSortCode: '',
      bankAccountNumber: '',
    });
    setPayoutEditing(true);
  };

  const handlePayoutSave = async () => {
    const scRaw = payoutForm.bankSortCode.trim();
    const anRaw = payoutForm.bankAccountNumber.trim();
    const sc = scRaw.replace(/[^0-9]/g, '');
    const an = anRaw.replace(/[^0-9]/g, '');

    // Mask-echo guard: if the user somehow ended up with asterisks in
    // the input, refuse to send. The server also rejects this.
    if (scRaw.includes('*') || anRaw.includes('*')) {
      setError('Please re-enter your full sort code and account number, not the masked version.');
      return;
    }

    // Only validate non-empty fields. Empty means "leave unchanged".
    if (scRaw && sc.length !== 6) {
      setError('Sort code must be 6 digits');
      return;
    }
    if (anRaw && an.length !== 8) {
      setError('Account number must be 8 digits');
      return;
    }

    const payload = { bankAccountName: payoutForm.bankAccountName.trim() };
    if (sc) payload.bankSortCode = sc;
    if (an) payload.bankAccountNumber = an;

    try {
      setPayoutSaving(true);
      await restaurantAPI.updateProfile(payload, token);
      setPayoutEditing(false);
      setSuccess('Payout details updated');
      setTimeout(() => setSuccess(''), 3000);
      await loadProfile();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save payout details');
    } finally {
      setPayoutSaving(false);
    }
  };

  const toggleAllergen = (allergen) => {
    setMenuForm(prev => ({
      ...prev,
      allergens: prev.allergens.includes(allergen)
        ? prev.allergens.filter(a => a !== allergen)
        : [...prev.allergens, allergen],
      // Selecting an allergen contradicts a "contains none" declaration.
      containsNoAllergens: false
    }));
  };

  // Time since order was placed
  const timeSince = (dateStr) => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ${diff % 60}m ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">🍽</div>
          <p className="text-gray-600">Loading restaurant dashboard…</p>
        </div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const restaurant = dashboard?.restaurant || profile || {};
  const onboardingStatus = restaurant.onboardingStatus || 'pending_review';

  const activeOrders = orders.filter(o =>
    ['placed', 'accepted', 'preparing', 'ready_for_pickup', 'out_for_delivery'].includes(o.status)
  );
  const pastOrders = orders.filter(o =>
    ['delivered', 'rejected', 'cancelled'].includes(o.status)
  );
  const newOrders = orders.filter(o => o.status === 'placed');

  // Menu filtering
  const filteredMenu = menuItems.filter(item => {
    if (menuCategoryFilter && item.category !== menuCategoryFilter) return false;
    if (menuSearchQuery) {
      const q = menuSearchQuery.toLowerCase();
      return item.name?.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q);
    }
    return true;
  });

  // Menu grouped by category
  const menuCategories = [...new Set(menuItems.map(i => i.category))].sort();

  // Analytics calculations
  const avgOrderValue = pastOrders.length > 0
    ? (pastOrders.filter(o => o.status === 'delivered').reduce((sum, o) => sum + (o.total || 0), 0) / pastOrders.filter(o => o.status === 'delivered').length)
    : 0;
  const completionRate = orders.length > 0
    ? Math.round((orders.filter(o => o.status === 'delivered').length / orders.length) * 100)
    : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1F3A68] to-[#2d5299] flex items-center justify-center text-white text-2xl font-bold">
            {(restaurant.restaurantName || 'R')[0]}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{restaurant.restaurantName || 'Restaurant Dashboard'}</h1>
            <p className="text-gray-500 text-sm capitalize">
              {restaurant.businessType || 'Restaurant'} · {restaurant.city}
              {restaurant.openingHours && <span className="ml-2">🕐 {restaurant.openingHours}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            onboardingStatus === 'approved' ? 'bg-green-100 text-green-700'
            : onboardingStatus === 'rejected' ? 'bg-red-100 text-red-700'
            : 'bg-yellow-100 text-yellow-700'
          }`}>
            {onboardingStatus === 'approved' ? '✅ Live'
             : onboardingStatus === 'pending_review' ? '⏳ Under Review'
             : onboardingStatus === 'rejected' ? '❌ Rejected' : '📋 Pending'}
          </span>
          {restaurant.rating > 0 && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
              ⭐ {restaurant.rating?.toFixed(1)} ({restaurant.totalRatings})
            </span>
          )}
          {restaurant.compliance?.fhrsRating != null && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
              FHRS {restaurant.compliance.fhrsRating}★
            </span>
          )}
          {/* Dashboard Switcher & Sign Out */}
          <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-gray-200">
            <button
              onClick={() => navigate('/guest/browse')}
              className="px-3 py-1.5 text-xs font-medium text-[#1F3A68] bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              title="Switch to Guest Portal"
            >
              🏠 Guest Portal
            </button>
            <button
              onClick={() => {
                if (onLogout) {
                  onLogout();
                  navigate('/');
                }
              }}
              className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              title="Sign Out"
            >
              🚪 Sign Out
            </button>
          </div>
        </div>
      </div>

      {onboardingStatus !== 'approved' && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h3 className="font-semibold text-amber-900 mb-1">Application Under Review</h3>
          <p className="text-sm text-amber-700">
            Your food business registration, FHRS rating, and hygiene certificates are being verified by our compliance team.
            You'll receive an email once approved. Customers cannot place orders until approval is complete.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <span className="text-red-500">⚠️</span>
          <span className="text-red-700 text-sm flex-1">{error}</span>
          <button className="text-red-400 hover:text-red-600" onClick={() => setError('')}>✕</button>
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">✅ {success}</div>
      )}

      {/* Coming-soon banner — only renders for platform_delivery
          restaurants that have not yet been promoted to live. Shows
          live rider coverage progress in the restaurant's city and a
          shareable referral code to help recruit riders. Hidden once
          the area crosses the threshold. */}
      {coverage && !coverage.isReady && coverage.fulfillmentMode === 'platform_delivery' && (() => {
        const total      = Math.max(coverage.threshold || 5, 1);
        const have       = Math.min(coverage.riderCount || 0, total);
        const pct        = Math.round((have / total) * 100);
        const remaining  = Math.max(0, total - have);
        const refLink    = coverage.referralCode
          ? `${window.location.origin}/signup?ref=${coverage.referralCode}`
          : null;
        return (
          <div className="mb-4 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-5">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-2xl">🛵</span>
              <div className="flex-1">
                <p className="font-bold text-amber-900">Coming soon in {coverage.city || 'your area'}</p>
                <p className="text-xs text-amber-900/80 mt-0.5 leading-relaxed">
                  Ariv is still building our rider network. Your restaurant goes live the moment {total} verified
                  riders sign up in {coverage.city || 'your area'}. <strong>Help us get there {remaining} rider{remaining !== 1 ? 's' : ''} sooner</strong> by sharing your referral link with anyone who might want to deliver for Ariv. You earn referral rewards when they join.
                </p>
              </div>
            </div>
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-amber-900 mb-1">
                <span>{have} of {total} riders verified in {coverage.city || 'your city'}</span>
                <span>{pct}%</span>
              </div>
              <div className="h-2 bg-white/70 rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
            {refLink ? (
              <div className="flex items-center gap-2 bg-white border border-amber-200 rounded-lg p-2">
                <code className="text-xs text-gray-800 flex-1 truncate font-mono px-1">{refLink}</code>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(refLink);
                      setSuccess('Referral link copied');
                      setTimeout(() => setSuccess(''), 2500);
                    } catch (_) {}
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded"
                >
                  Copy link
                </button>
              </div>
            ) : (
              <p className="text-xs text-amber-900/70 italic">
                Your referral link is being generated — it will appear here once your account is fully set up.
              </p>
            )}
          </div>
        );
      })()}

      {/* Fulfillment Mode Banner */}
      <div className={`mb-4 rounded-xl border p-4 flex items-center justify-between ${
        stats.fulfillmentMode === 'self_delivery'
          ? 'bg-green-50 border-green-200'
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">{stats.fulfillmentMode === 'self_delivery' ? '🏪' : '🛵'}</span>
          <div>
            <p className={`font-bold text-sm ${stats.fulfillmentMode === 'self_delivery' ? 'text-green-800' : 'text-blue-800'}`}>
              {stats.fulfillmentMode === 'self_delivery' ? 'Self-Delivery Model' : 'Platform Delivery Model'}
            </p>
            <p className="text-xs text-gray-600">
              {stats.fulfillmentMode === 'self_delivery'
                ? `${((stats.commissionRate || 0.14) * 100).toFixed(0)}% commission · You handle delivery · 5% service fee`
                : `${((stats.commissionRate || 0.25) * 100).toFixed(0)}% commission · Platform riders deliver · Tiered fees (max £4.99)`
              }
            </p>
          </div>
        </div>
        {stats.totalCommissionPaid > 0 && (
          <div className="text-right">
            <p className="text-xs text-gray-500">Total Commission</p>
            <p className="font-bold text-sm text-gray-700">£{(stats.totalCommissionPaid || 0).toFixed(2)}</p>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'New Orders', value: stats.pendingOrders || 0, icon: '🔔', color: 'text-blue-600', urgent: (stats.pendingOrders || 0) > 0, bg: 'bg-blue-50' },
          { label: 'In Kitchen', value: stats.activeOrders || 0, icon: '👨‍🍳', color: 'text-orange-600', bg: 'bg-orange-50' },
          { label: 'Completed', value: stats.completedOrders || 0, icon: '✅', color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Revenue', value: `£${(stats.totalRevenue || 0).toFixed(2)}`, icon: '💰', color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Menu Items', value: stats.menuItems || 0, icon: '🍽', color: 'text-gray-600', bg: 'bg-gray-50' },
        ].map(card => (
          <div key={card.label} className={`${card.bg} rounded-xl border p-4 text-center ${card.urgent ? 'border-blue-400 shadow-md shadow-blue-100 animate-pulse' : 'border-gray-100'}`}>
            <div className="text-xl mb-1">{card.icon}</div>
            <div className={`text-xl font-bold ${card.color}`}>{card.value}</div>
            <div className="text-xs text-gray-500">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 gap-1 overflow-x-auto">
        {[
          { key: 'orders', label: '📦 Orders', count: newOrders.length, countColor: 'bg-red-500' },
          { key: 'menu', label: '🍽 Menu', count: menuItems.length, countColor: 'bg-blue-600' },
          { key: 'messages', label: '📬 Messages', count: unreadCount, countColor: 'bg-green-600' },
          { key: 'analytics', label: '📊 Analytics' },
          { key: 'promos', label: '🎟 Promos', count: promoCodes.filter(p => p.isActive).length, countColor: 'bg-purple-600' },
          { key: 'settings', label: '⚙️ Settings' },
          { key: 'faq', label: '❓ FAQ' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              tab === t.key ? 'border-[#1F3A68] text-[#1F3A68]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className={`${t.countColor} text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center`}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Accept Order Modal (Prep Time) */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAcceptModal(null)}>
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg text-gray-900 mb-2">Accept Order</h3>
            <p className="text-sm text-gray-600 mb-4">Set estimated preparation time for the customer:</p>
            <div className="flex gap-2 mb-4">
              {[15, 25, 35, 45, 60].map(t => (
                <button key={t} onClick={() => setPrepTimeInput(String(t))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border ${prepTimeInput === String(t) ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >{t}m</button>
              ))}
            </div>
            <input type="number" placeholder="Custom (minutes)" value={prepTimeInput} onChange={e => setPrepTimeInput(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-green-500" />
            <div className="flex gap-2">
              <button onClick={() => setShowAcceptModal(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600">Cancel</button>
              <button onClick={() => handleAcceptWithPrepTime(showAcceptModal)} className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm font-bold hover:bg-green-600">
                ✅ Accept Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Order Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowRejectModal(null)}>
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg text-red-700 mb-2">Reject Order</h3>
            <p className="text-sm text-gray-600 mb-3">Please provide a reason (the customer will be notified):</p>
            <select value={rejectionReasonInput} onChange={e => setRejectionReasonInput(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-red-400">
              <option value="">Select a reason…</option>
              <option value="Restaurant is too busy">Restaurant is too busy</option>
              <option value="Item(s) unavailable">Item(s) unavailable</option>
              <option value="Kitchen closed early">Kitchen closed early</option>
              <option value="Delivery address out of range">Delivery address out of range</option>
              <option value="Other">Other</option>
            </select>
            {rejectionReasonInput === 'Other' && (
              <input type="text" placeholder="Specify reason…" onChange={e => setRejectionReasonInput(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-red-400" />
            )}
            <div className="flex gap-2">
              <button onClick={() => { setShowRejectModal(null); setRejectionReasonInput(''); }} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-600">Cancel</button>
              <button onClick={() => handleRejectWithReason(showRejectModal)} disabled={!rejectionReasonInput}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 disabled:opacity-50">
                ❌ Reject Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ ORDERS TAB ══════════════ */}
      {tab === 'orders' && (
        <div>
          {/* Order filter tabs */}
          <div className="flex gap-2 mb-4">
            {[
              { key: 'active', label: `Active (${activeOrders.length})` },
              { key: 'history', label: `History (${pastOrders.length})` },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setOrderFilter(f.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  orderFilter === f.key ? 'bg-[#1F3A68] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
            <button onClick={loadOrders} className="ml-auto px-3 py-1.5 rounded-full text-sm text-gray-500 hover:bg-gray-100" title="Refresh">🔄 Refresh</button>
          </div>

          {/* Active Orders */}
          {orderFilter === 'active' && (
            <>
              {/* New orders get special treatment */}
              {newOrders.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold text-red-600 mb-3 flex items-center gap-2">
                    <span className="animate-ping inline-flex h-2 w-2 rounded-full bg-red-500" />
                    New Orders — Action Required ({newOrders.length})
                  </h3>
                  <div className="space-y-3">
                    {newOrders.map(order => renderOrderCard(order, true))}
                  </div>
                </div>
              )}

              {/* In-progress orders */}
              {activeOrders.filter(o => o.status !== 'placed').length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-800 mb-3">In Progress ({activeOrders.filter(o => o.status !== 'placed').length})</h3>
                  <div className="space-y-3">
                    {activeOrders.filter(o => o.status !== 'placed').map(order => renderOrderCard(order, false))}
                  </div>
                </div>
              )}

              {activeOrders.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                  <div className="text-5xl mb-3">📭</div>
                  <p className="font-medium text-lg">No active orders right now</p>
                  <p className="text-sm mt-1">New orders will appear here automatically</p>
                  <p className="text-xs mt-3 text-gray-300">Auto-refreshing every 15 seconds</p>
                </div>
              )}
            </>
          )}

          {/* Order History */}
          {orderFilter === 'history' && (
            <>
              {pastOrders.length > 0 ? (
                <div className="space-y-2">
                  {pastOrders.slice(0, 50).map(order => {
                    const statusInfo = ORDER_STATUS_FLOW[order.status];
                    return (
                      <div key={order._id} className="bg-white rounded-xl border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{statusInfo?.icon}</span>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-gray-900 text-sm">#{order.orderNumber}</span>
                                {order.pickupCode && (
                                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-mono font-bold border border-amber-300" title="Pickup Code">
                                    📦 {order.pickupCode?.slice(0,3)}-{order.pickupCode?.slice(3)}
                                  </span>
                                )}
                                <span className={`px-2 py-0.5 rounded-full text-xs ${statusInfo?.color}`}>
                                  {order.status?.replace(/_/g, ' ')}
                                </span>
                                {order.rating && (
                                  <span className="text-xs text-yellow-600">{'⭐'.repeat(order.rating)}</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {order.guestName} · {order.items?.length} item{order.items?.length !== 1 ? 's' : ''} · {new Date(order.createdAt).toLocaleDateString('en-GB')} {new Date(order.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">£{order.total?.toFixed(2)}</p>
                            <p className="text-xs text-gray-400">{order.deliveryType === 'collection' ? '🏃 Collection' : '🛵 Delivery'}</p>
                          </div>
                        </div>
                        {expandedOrder === order._id && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="bg-gray-50 rounded-lg p-3 text-sm">
                              {order.items?.map((item, i) => (
                                <div key={i} className="flex justify-between text-gray-700 py-0.5">
                                  <span>{item.quantity}× {item.name}</span>
                                  <span>£{(item.price * item.quantity).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                            {order.deliveryAddress && order.deliveryType !== 'collection' && (
                              <p className="text-xs text-gray-500 mt-2">📍 {order.deliveryAddress}</p>
                            )}
                            {order.notes && <p className="text-xs text-gray-500 mt-1">📝 {order.notes}</p>}
                          </div>
                        )}
                        <button
                          onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}
                          className="text-xs text-blue-600 mt-2 hover:underline"
                        >
                          {expandedOrder === order._id ? 'Hide details' : 'View details'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <p>No order history yet</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══════════════ MENU TAB ══════════════ */}
      {tab === 'menu' && (
        <div>
          {/* Menu toolbar */}
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                type="text"
                placeholder="Search menu items…"
                value={menuSearchQuery}
                onChange={e => setMenuSearchQuery(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
              />
              <select
                value={menuCategoryFilter}
                onChange={e => setMenuCategoryFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none w-40"
              >
                <option value="">All Categories</option>
                {menuCategories.map(c => (
                  <option key={c} value={c}>{c} ({menuItems.filter(i => i.category === c).length})</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => openMenuForm()}
              className="px-5 py-2.5 bg-[#1F3A68] hover:bg-[#1F3A68]/90 text-white rounded-lg text-sm font-semibold flex items-center gap-2 flex-shrink-0"
            >
              + Add Menu Item
            </button>
          </div>

          {/* Menu item form modal */}
          {showMenuForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
                  <h3 className="font-bold text-gray-900 text-lg">{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</h3>
                  <button onClick={() => setShowMenuForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                </div>
                <form onSubmit={handleMenuSubmit} className="p-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
                    <input
                      type="text"
                      required
                      value={menuForm.name}
                      onChange={e => setMenuForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Margherita Pizza"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                      <select
                        required
                        value={menuForm.category}
                        onChange={e => setMenuForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select category</option>
                        {MENU_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price (£) *</label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        min="0.01"
                        value={menuForm.price}
                        onChange={e => setMenuForm(prev => ({ ...prev, price: e.target.value }))}
                        placeholder="0.00"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea
                      required
                      value={menuForm.description}
                      onChange={e => setMenuForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe the dish, ingredients, and what makes it special…"
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  {/* Image upload + URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Item Photo</label>
                    <div className="flex gap-3 items-start">
                      {menuForm.imageUrl && (
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                          <img src={resolveImg(menuForm.imageUrl)} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                      )}
                      <div className="flex-1 space-y-2">
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          disabled={imageUploading}
                          className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                        >
                          {imageUploading ? '⏳ Uploading…' : '📷 Upload Image'}
                        </button>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">OR URL:</span>
                          <input
                            type="text"
                            value={menuForm.imageUrl || ''}
                            onChange={e => setMenuForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                            placeholder="https://example.com/photo.jpg"
                            className="w-full border border-gray-300 rounded-lg pl-16 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Calories</label>
                      <input
                        type="number"
                        min="0"
                        value={menuForm.calories || ''}
                        onChange={e => setMenuForm(prev => ({ ...prev, calories: e.target.value }))}
                        placeholder="e.g. 450"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prep Time (mins)</label>
                      <input
                        type="number"
                        min="0"
                        value={menuForm.prepTime || ''}
                        onChange={e => setMenuForm(prev => ({ ...prev, prepTime: e.target.value }))}
                        placeholder="e.g. 15"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Allergens */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allergens <span className="text-xs text-red-500">(UK 14 Major Allergens — required by law)</span>
                    </label>
                    <p className="text-xs text-gray-500 mb-2">Select all allergens present in this dish. Under the Food Information Regulations 2014, you must declare these.</p>
                    <div className="flex flex-wrap gap-1.5">
                      {UK_ALLERGENS.map(a => (
                        <button
                          key={a}
                          type="button"
                          onClick={() => toggleAllergen(a)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium border capitalize transition-colors ${
                            menuForm.allergens.includes(a)
                              ? 'bg-red-100 text-red-700 border-red-300'
                              : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={menuForm.containsNoAllergens}
                        onChange={e => setMenuForm(prev => ({
                          ...prev,
                          containsNoAllergens: e.target.checked,
                          allergens: e.target.checked ? [] : prev.allergens
                        }))}
                        className="w-4 h-4 rounded border-gray-300 mt-0.5"
                      />
                      <span className="text-xs text-gray-700">
                        I confirm this dish contains <strong>none of the 14 major allergens</strong>.
                        (You must either select the allergens above or tick this — items without an
                        allergen declaration cannot be published.)
                      </span>
                    </label>
                  </div>

                  {/* Dietary flags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dietary Tags</label>
                    <div className="flex flex-wrap gap-3">
                      {[
                        { key: 'isVegan', label: '🌿 Vegan' },
                        { key: 'isVegetarian', label: '🥦 Vegetarian' },
                        { key: 'isGlutenFree', label: 'GF Gluten Free' },
                        { key: 'isHalal', label: '☪ Halal' },
                      ].map(flag => (
                        <label key={flag.key} className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={menuForm[flag.key]}
                            onChange={e => setMenuForm(prev => ({ ...prev, [flag.key]: e.target.checked }))}
                            className="rounded"
                          />
                          {flag.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spice Level</label>
                      <select
                        value={menuForm.spiceLevel}
                        onChange={e => setMenuForm(prev => ({ ...prev, spiceLevel: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                      >
                        <option value={0}>None</option>
                        <option value={1}>🌶 Mild</option>
                        <option value={2}>🌶🌶 Medium</option>
                        <option value={3}>🌶🌶🌶 Hot</option>
                      </select>
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={menuForm.isAvailable}
                          onChange={e => setMenuForm(prev => ({ ...prev, isAvailable: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">Available now</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-3 border-t border-gray-100">
                    <button type="button" onClick={() => setShowMenuForm(false)} className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button type="submit" className="flex-1 py-2.5 bg-[#1F3A68] text-white rounded-lg text-sm font-semibold hover:bg-[#1F3A68]/90">{editingItem ? 'Save Changes' : 'Add to Menu'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Menu items list */}
          {filteredMenu.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">🍽</div>
              <p className="font-medium text-lg">{menuItems.length === 0 ? 'No menu items yet' : 'No items match your search'}</p>
              {menuItems.length === 0 && (
                <button onClick={() => openMenuForm()} className="mt-4 px-6 py-2.5 bg-[#1F3A68] text-white rounded-lg text-sm font-semibold">Add Your First Item</button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMenu.map(item => (
                <div key={item._id || item.id} className={`bg-white rounded-xl border p-4 flex items-center gap-4 transition-colors ${item.isAvailable ? 'border-gray-200' : 'border-red-200 bg-red-50/30'}`}>
                  <div className="w-16 h-16 rounded-lg bg-orange-50 flex-shrink-0 flex items-center justify-center overflow-hidden">
                    {item.imageUrl ? (
                      <img src={resolveImg(item.imageUrl)} alt={item.name} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <span className="text-3xl">🍽</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">{item.name}</h4>
                      <span className="text-sm font-bold text-[#1F3A68]">£{item.price?.toFixed(2)}</span>
                      {!item.isAvailable && <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">UNAVAILABLE</span>}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleItemAvailability(item._id, item.isAvailable); }}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${item.isAvailable !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}
                      >{item.isAvailable !== false ? '● In Stock' : '○ Out of Stock'}</button>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{item.category}{item.prepTime ? ` · ⏱ ${item.prepTime}min` : ''}{item.calories ? ` · ${item.calories}kcal` : ''}</p>
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{item.description}</p>
                    {item.allergens?.length > 0 && (
                      <p className="text-xs text-amber-600 mt-0.5">⚠ Contains: {item.allergens.join(', ')}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.isVegan && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">🌿 Vegan</span>}
                      {item.isVegetarian && !item.isVegan && <span className="text-xs bg-green-50 text-green-600 px-1.5 py-0.5 rounded">🥦 Veggie</span>}
                      {item.isGlutenFree && <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">GF</span>}
                      {item.isHalal && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">☪ Halal</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => toggleItemAvailability(item)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        item.isAvailable
                          ? 'text-orange-600 border-orange-200 hover:bg-orange-50'
                          : 'text-green-600 border-green-200 hover:bg-green-50'
                      }`}
                    >
                      {item.isAvailable ? '⏸ Pause' : '▶ Enable'}
                    </button>
                    <button onClick={() => openMenuForm(item)} className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg text-xs font-medium border border-blue-200">✏️ Edit</button>
                    <button onClick={() => handleDeleteItem(item._id || item.id)} className="px-3 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs font-medium border border-red-200">🗑 Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ ANALYTICS TAB ══════════════ */}
      {tab === 'analytics' && (
        <div className="space-y-6">
          {/* ── Key Metrics Row ── */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Total Orders', value: menuPerformance?.orderStats?.total || stats.totalOrders || 0, icon: '📦', color: 'bg-blue-50 border-blue-200' },
              { label: 'Completion Rate', value: `${menuPerformance?.orderStats?.completionRate || completionRate}%`, icon: '✅', color: 'bg-green-50 border-green-200' },
              { label: 'Cancellation Rate', value: `${menuPerformance?.orderStats?.cancellationRate || 0}%`, icon: '❌', color: 'bg-red-50 border-red-200' },
              { label: 'Avg Order Value', value: `£${(menuPerformance?.revenueStats?.avgOrderValue || avgOrderValue).toFixed(2)}`, icon: '💷', color: 'bg-amber-50 border-amber-200' },
              { label: 'Customer Rating', value: menuPerformance?.customerStats?.avgRating ? `⭐ ${menuPerformance.customerStats.avgRating}` : 'No reviews', icon: '⭐', color: 'bg-yellow-50 border-yellow-200' },
            ].map(m => (
              <div key={m.label} className={`${m.color} border rounded-xl p-4 text-center`}>
                <div className="text-xl mb-1">{m.icon}</div>
                <div className="text-xl font-bold text-gray-900">{m.value}</div>
                <div className="text-[10px] text-gray-500 mt-1 uppercase tracking-wide">{m.label}</div>
              </div>
            ))}
          </div>

          {/* ── Revenue & Profit ── */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-4">💰 Revenue & Profit</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm text-gray-600">Gross Revenue</span>
                  <span className="font-bold text-lg">£{(menuPerformance?.revenueStats?.grossRevenue || stats.totalRevenue || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm text-gray-600">
                    Platform Commission ({((stats.commissionRate || 0.14) * 100).toFixed(0)}%)
                    <span className="text-xs text-gray-400 ml-1">
                      ({stats.fulfillmentMode === 'self_delivery' ? 'Self-Delivery' : 'Platform Delivery'})
                    </span>
                  </span>
                  <span className="font-semibold text-red-600">-£{(menuPerformance?.revenueStats?.commission || stats.totalCommissionPaid || (stats.totalRevenue || 0) * (stats.commissionRate || 0.14)).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-sm text-gray-600">VAT Collected</span>
                  <span className="font-semibold text-gray-500">£{(menuPerformance?.revenueStats?.totalVAT || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-green-50 rounded-lg px-3">
                  <span className="text-sm font-bold text-green-800">Net Earnings</span>
                  <span className="font-bold text-green-700 text-xl">£{(menuPerformance?.revenueStats?.netEarnings || (stats.totalRevenue || 0) * (1 - (stats.commissionRate || 0.14))).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Order Status Breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-4">📊 Order Breakdown</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { label: 'Delivered', count: menuPerformance?.orderStats?.delivered || orders.filter(o => o.status === 'delivered').length, color: 'bg-green-100 text-green-700', icon: '✅' },
                  { label: 'Cancelled', count: menuPerformance?.orderStats?.cancelled || orders.filter(o => ['cancelled','rejected'].includes(o.status)).length, color: 'bg-red-100 text-red-700', icon: '❌' },
                  { label: 'Active Now', count: menuPerformance?.orderStats?.active || activeOrders.length, color: 'bg-blue-100 text-blue-700', icon: '🔄' },
                  { label: 'Complaints', count: menuPerformance?.customerStats?.complaints || 0, color: 'bg-orange-100 text-orange-700', icon: '⚠️' },
                ].map(s => (
                  <div key={s.label} className={`${s.color} rounded-lg p-3 text-center`}>
                    <div className="text-lg">{s.icon}</div>
                    <div className="text-2xl font-bold">{s.count}</div>
                    <div className="text-xs font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
              {/* Customer feedback summary */}
              <div className="bg-gray-50 rounded-lg p-3 mt-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Reviews</span>
                  <span className="font-semibold">{menuPerformance?.customerStats?.totalReviews || 0}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-gray-600">Avg Rating</span>
                  <span className="font-semibold">{menuPerformance?.customerStats?.avgRating ? `⭐ ${menuPerformance.customerStats.avgRating} / 5` : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── 7-Day Trend ── */}
          {menuPerformance?.dailyTrend?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-4">📈 Last 7 Days</h3>
              <div className="grid grid-cols-7 gap-2">
                {menuPerformance.dailyTrend.map(d => {
                  const maxOrders = Math.max(...menuPerformance.dailyTrend.map(x => x.orders), 1);
                  const height = Math.max(20, (d.orders / maxOrders) * 100);
                  const dayName = new Date(d.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short' });
                  return (
                    <div key={d.date} className="flex flex-col items-center">
                      <div className="text-xs font-bold text-gray-900 mb-1">{d.orders}</div>
                      <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '100px' }}>
                        <div
                          className="absolute bottom-0 w-full bg-gradient-to-t from-[#1F3A68] to-[#3d6bb5] rounded-t-lg transition-all"
                          style={{ height: `${height}%` }}
                        />
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">{dayName}</div>
                      <div className="text-[10px] text-green-600 font-semibold">£{d.revenue.toFixed(0)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Menu Performance Leaderboard ── */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4">🏆 Menu Leaderboard</h3>

            {/* Best Seller & Most Profitable highlights */}
            {menuPerformance && (menuPerformance.bestSeller || menuPerformance.mostProfitable) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {menuPerformance.bestSeller && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🥇</span>
                      <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Best Seller</span>
                    </div>
                    <p className="font-bold text-gray-900 text-lg">{menuPerformance.bestSeller.name}</p>
                    <p className="text-sm text-amber-700 font-semibold mt-1">{menuPerformance.bestSeller.totalOrdered} orders · £{menuPerformance.bestSeller.totalRevenue.toFixed(2)} revenue</p>
                  </div>
                )}
                {menuPerformance.mostProfitable && menuPerformance.mostProfitable.name !== menuPerformance.bestSeller?.name ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">💰</span>
                      <span className="text-xs font-bold text-green-700 uppercase tracking-wide">Most Profitable</span>
                    </div>
                    <p className="font-bold text-gray-900 text-lg">{menuPerformance.mostProfitable.name}</p>
                    <p className="text-sm text-green-700 font-semibold mt-1">£{menuPerformance.mostProfitable.totalRevenue.toFixed(2)} revenue · {menuPerformance.mostProfitable.totalOrdered} sold</p>
                  </div>
                ) : menuPerformance.mostProfitable && (
                  <div className="bg-gradient-to-br from-amber-50 to-green-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">⭐</span>
                      <span className="text-xs font-bold text-green-700 uppercase tracking-wide">Also Most Profitable!</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">Your #1 seller also makes you the most money — a winning combination!</p>
                  </div>
                )}
              </div>
            )}

            {/* Ranked items table */}
            {menuPerformance?.items?.length > 0 ? (
              <div>
                <div className="grid grid-cols-12 gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2 mb-2">
                  <span className="col-span-1">#</span>
                  <span className="col-span-4">Item</span>
                  <span className="col-span-2 text-right">Orders</span>
                  <span className="col-span-3 text-right">Revenue</span>
                  <span className="col-span-2 text-right">Avg Price</span>
                </div>
                {menuPerformance.items.map((item, idx) => (
                  <div key={item.name} className={`grid grid-cols-12 gap-2 items-center py-2.5 px-2 rounded-lg ${idx < 3 ? 'bg-gray-50' : ''} border-b border-gray-50 last:border-0`}>
                    <span className={`col-span-1 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-amber-400 text-white' : idx === 1 ? 'bg-gray-400 text-white' : idx === 2 ? 'bg-orange-400 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>{idx + 1}</span>
                    <span className="col-span-4 text-sm font-medium text-gray-800 truncate">{item.name}</span>
                    <span className="col-span-2 text-sm text-right font-semibold text-gray-700">{item.totalOrdered}</span>
                    <span className="col-span-3 text-sm text-right font-bold text-green-700">£{item.totalRevenue.toFixed(2)}</span>
                    <span className="col-span-2 text-xs text-right text-gray-500">£{item.avgPrice.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : menuItems.length > 0 ? (
              <div className="space-y-2">
                {menuItems.slice(0, 10).map((item, idx) => (
                  <div key={item._id || item.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                    <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">{idx + 1}</span>
                    <span className="flex-1 text-sm font-medium text-gray-800">{item.name}</span>
                    <span className="text-sm text-gray-500">{item.category}</span>
                    <span className="font-semibold text-sm">£{item.price?.toFixed(2)}</span>
                  </div>
                ))}
                <p className="text-xs text-gray-400 text-center py-2">Complete order data will appear here once customers start ordering</p>
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-4">Add menu items and receive orders to see leaderboard data</p>
            )}
          </div>

          {/* Payout Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">💰 Payout Summary</h3>
              <div className="flex gap-1">
                {['today', 'week', 'month', 'all'].map(p => (
                  <button key={p} onClick={() => { setPayoutPeriod(p); loadPayoutSummary(p); }}
                    className={`px-3 py-1 rounded-lg text-xs font-medium ${payoutPeriod === p ? 'bg-[#1F3A68] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {p === 'all' ? 'All Time' : p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {payoutSummary ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-green-600 font-semibold uppercase">Gross Revenue</p>
                  <p className="text-xl font-bold text-green-800">£{payoutSummary.grossRevenue?.toFixed(2)}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-red-600 font-semibold uppercase">Commission ({payoutSummary.commissionRate})</p>
                  <p className="text-xl font-bold text-red-700">-£{payoutSummary.commission?.toFixed(2)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-600 font-semibold uppercase">Tips</p>
                  <p className="text-xl font-bold text-blue-800">£{payoutSummary.totalTips?.toFixed(2)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-purple-600 font-semibold uppercase">Est. Payout</p>
                  <p className="text-xl font-bold text-purple-800">£{payoutSummary.estimatedPayout?.toFixed(2)}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 text-sm text-center py-4">Loading payout data…</p>
            )}
          </div>
        </div>
      )}

      {/* ══════════════ PROMOS TAB ══════════════ */}
      {tab === 'promos' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg text-gray-800">Promotional Codes</h2>
            <button onClick={() => setShowPromoForm(!showPromoForm)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors">
              {showPromoForm ? 'Cancel' : '➕ Create Promo'}
            </button>
          </div>

          {showPromoForm && (
            <form onSubmit={handleCreatePromo} className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Promo Code *</label>
                  <input value={promoForm.code} onChange={e => setPromoForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="e.g. WELCOME20"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Discount Type</label>
                  <select value={promoForm.discountType} onChange={e => setPromoForm(p => ({ ...p, discountType: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (£)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Discount Value *</label>
                  <input type="number" step="0.01" value={promoForm.discountValue} onChange={e => setPromoForm(p => ({ ...p, discountValue: e.target.value }))}
                    placeholder={promoForm.discountType === 'percentage' ? 'e.g. 10' : 'e.g. 5.00'} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Min Order Value (£)</label>
                  <input type="number" step="0.01" value={promoForm.minOrderValue} onChange={e => setPromoForm(p => ({ ...p, minOrderValue: e.target.value }))}
                    placeholder="0" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Max Total Uses</label>
                  <input type="number" value={promoForm.maxUses} onChange={e => setPromoForm(p => ({ ...p, maxUses: e.target.value }))}
                    placeholder="Unlimited" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Expires On</label>
                  <input type="date" value={promoForm.validUntil} onChange={e => setPromoForm(p => ({ ...p, validUntil: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex items-center gap-4 mt-4">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={promoForm.firstOrderOnly} onChange={e => setPromoForm(p => ({ ...p, firstOrderOnly: e.target.checked }))}
                    className="rounded" />
                  First order only
                </label>
                <input value={promoForm.description} onChange={e => setPromoForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Description (optional)" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <button type="submit" className="mt-4 px-6 py-2.5 bg-purple-600 text-white rounded-lg font-bold text-sm hover:bg-purple-700">Create Promo Code</button>
            </form>
          )}

          <div className="space-y-3">
            {promoCodes.length === 0 ? (
              <div className="text-center py-10 bg-gray-50 rounded-xl">
                <p className="text-3xl mb-2">🎟</p>
                <p className="text-gray-600 font-medium">No promo codes yet</p>
                <p className="text-gray-400 text-sm mt-1">Create promo codes to attract customers and boost orders</p>
              </div>
            ) : (
              promoCodes.map(promo => (
                <div key={promo._id} className={`bg-white rounded-xl border-2 p-4 ${promo.isActive ? 'border-purple-200' : 'border-gray-200 opacity-60'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-lg font-mono font-bold text-sm tracking-wider">{promo.code}</span>
                      <div>
                        <p className="font-semibold text-gray-800">
                          {promo.discountType === 'percentage' ? `${promo.discountValue}% off` : `£${promo.discountValue} off`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {promo.minOrderValue > 0 && `Min £${promo.minOrderValue} · `}
                          Used {promo.timesUsed || 0}{promo.maxUses ? `/${promo.maxUses}` : ''} times
                          {promo.firstOrderOnly && ' · First order only'}
                          {promo.validUntil && ` · Expires ${new Date(promo.validUntil).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={async () => { await restaurantAPI.updatePromoCode(promo._id, { isActive: !promo.isActive }, token); loadPromoCodes(); }}
                        className={`px-3 py-1 rounded-lg text-xs font-medium ${promo.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {promo.isActive ? '✅ Active' : '⏸ Paused'}
                      </button>
                      <button onClick={async () => { if (window.confirm('Delete this promo code?')) { await restaurantAPI.deletePromoCode(promo._id, token); loadPromoCodes(); } }}
                        className="px-2 py-1 text-red-400 hover:text-red-600 text-xs">🗑</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ══════════════ MESSAGES TAB ══════════════ */}
      {tab === 'messages' && (
        <div className="grid md:grid-cols-3 gap-0 border border-gray-200 rounded-xl overflow-hidden" style={{ height: '600px' }}>
          {/* ── Conversation List ── */}
          <div className="md:col-span-1 border-r border-gray-200 bg-gray-50 overflow-y-auto">
            <div className="p-3 border-b border-gray-200 bg-white sticky top-0 z-10">
              <h3 className="font-semibold text-gray-800 text-sm">Customer Messages</h3>
              <p className="text-xs text-gray-400 mt-0.5">{conversations.length} conversation{conversations.length !== 1 ? 's' : ''}</p>
            </div>
            {conversations.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-3xl mb-2">📭</p>
                <p className="text-sm text-gray-500 font-medium">No messages yet</p>
                <p className="text-xs text-gray-400 mt-1">Messages from customers about their orders will appear here</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {conversations.map(conv => {
                  const isSelected = selectedConversation?._id === conv._id;
                  const hasUnread = (conv.unreadHost || 0) > 0;
                  const timeAgo = conv.lastMessageAt ? (() => {
                    const diff = Date.now() - new Date(conv.lastMessageAt).getTime();
                    const mins = Math.floor(diff / 60000);
                    if (mins < 1) return 'now';
                    if (mins < 60) return `${mins}m`;
                    const hrs = Math.floor(mins / 60);
                    if (hrs < 24) return `${hrs}h`;
                    return `${Math.floor(hrs / 24)}d`;
                  })() : '';

                  return (
                    <button
                      key={conv._id}
                      onClick={() => setSelectedConversation(conv)}
                      className={`w-full text-left p-3 hover:bg-white transition-colors ${
                        isSelected ? 'bg-white border-l-4 border-l-[#1F3A68]' : hasUnread ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-sm text-gray-800 truncate">
                              {conv.guestName || 'Customer'}
                            </span>
                            {hasUnread && (
                              <span className="bg-green-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold flex-shrink-0">
                                {conv.unreadHost}
                              </span>
                            )}
                          </div>
                          {conv.conversationType === 'delivery_pickup' ? (
                            <p className="text-[10px] text-purple-600 font-medium mt-0.5">🛵 Delivery Rider</p>
                          ) : conv.relatedId && (
                            <p className="text-[10px] text-blue-600 font-medium mt-0.5">🍴 Food Order</p>
                          )}
                          <p className={`text-xs mt-0.5 truncate ${hasUnread ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                            {conv.lastMessage || 'No messages yet'}
                          </p>
                        </div>
                        <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">{timeAgo}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Message Thread ── */}
          <div className="md:col-span-2 flex flex-col bg-white">
            {selectedConversation ? (
              <>
                {/* Header */}
                <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{selectedConversation.guestName || 'Customer'}</p>
                    <p className="text-xs text-gray-500">
                      {selectedConversation.relatedType === 'food_order' ? '🍴 Food Order Conversation' : 'Direct Message'}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded"
                  >
                    ← Back
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 0 }}>
                  {messages.length === 0 ? (
                    <div className="text-center py-10">
                      <p className="text-gray-400 text-sm">No messages in this conversation yet</p>
                    </div>
                  ) : (
                    messages.map((msg, i) => {
                      const isMe = msg.senderId === user?._id || msg.senderId === user?.id;
                      const time = msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
                      const date = msg.createdAt ? new Date(msg.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
                      // Show date separator if first message or different day
                      const prevDate = i > 0 && messages[i-1].createdAt ? new Date(messages[i-1].createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
                      const showDate = i === 0 || date !== prevDate;

                      return (
                        <React.Fragment key={msg._id || i}>
                          {showDate && (
                            <div className="flex justify-center my-2">
                              <span className="text-[10px] text-gray-400 bg-gray-100 px-3 py-0.5 rounded-full">{date}</span>
                            </div>
                          )}
                          <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${
                              isMe
                                ? 'bg-[#1F3A68] text-white rounded-br-md'
                                : 'bg-gray-100 text-gray-800 rounded-bl-md'
                            }`}>
                              {!isMe && (
                                <p className="text-[10px] font-semibold text-gray-500 mb-0.5">{msg.senderName || 'Customer'}</p>
                              )}
                              <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                              <p className={`text-[10px] mt-1 ${isMe ? 'text-blue-200' : 'text-gray-400'} text-right`}>{time}</p>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                      placeholder="Type a reply…"
                      className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68] focus:border-transparent"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendingMessage}
                      className="px-5 py-2.5 bg-[#1F3A68] text-white rounded-xl text-sm font-semibold hover:bg-[#2d5299] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {sendingMessage ? '⏳' : '📤 Send'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-4xl mb-3">💬</p>
                  <p className="font-semibold text-gray-600">Select a conversation</p>
                  <p className="text-sm text-gray-400 mt-1">Choose a customer message to view and reply</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════ SETTINGS TAB ══════════════ */}
      {tab === 'settings' && profile && (
        <>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Business Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Business Details</h3>
              {!profileEditing ? (
                <button onClick={() => setProfileEditing(true)} className="text-sm text-[#1F3A68] hover:underline font-medium">Edit</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setProfileEditing(false)} className="text-sm text-gray-500 hover:underline">Cancel</button>
                  <button onClick={handleProfileSave} className="text-sm text-[#1F3A68] font-bold hover:underline">Save</button>
                </div>
              )}
            </div>

            {profileEditing ? (
              <div className="space-y-3">
                {[
                  { label: 'Restaurant Name', key: 'restaurantName' },
                  { label: 'Phone', key: 'phone' },
                  { label: 'City', key: 'city' },
                  { label: 'Address', key: 'address' },
                  { label: 'Postcode', key: 'postcode' },
                  { label: 'Opening Hours', key: 'openingHours', placeholder: 'e.g. 11:00-23:00' },
                  { label: 'Cuisine Types', key: 'cuisineTypes', placeholder: 'e.g. Italian, Pizza, Pasta' },
                  { label: 'Min Order (£)', key: 'minimumOrderValue', type: 'number' },
                  { label: 'Est. Delivery Time', key: 'estimatedDeliveryTime', placeholder: 'e.g. 30-45 mins' },
                  { label: 'Delivery Radius (miles)', key: 'deliveryRadius', type: 'number' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{field.label}</label>
                    <input
                      type={field.type || 'text'}
                      placeholder={field.placeholder}
                      value={profileForm[field.key] || ''}
                      onChange={e => setProfileForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Restaurant Description</label>
                  <textarea
                    value={profileForm.description || ''}
                    onChange={e => setProfileForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Tell customers about your restaurant, what makes it special…"
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1 text-sm">
                {[
                  { label: 'Name', value: profile.restaurantName },
                  { label: 'Type', value: profile.businessType },
                  { label: 'Phone', value: profile.phone },
                  { label: 'City', value: profile.city },
                  { label: 'Address', value: profile.address },
                  { label: 'Postcode', value: profile.postcode },
                  { label: 'Hours', value: profile.openingHours },
                  { label: 'Cuisines', value: profile.cuisineTypes?.join(', ') },
                  { label: 'Min Order', value: profile.minimumOrderValue ? `£${profile.minimumOrderValue}` : null },
                  { label: 'Delivery Time', value: profile.estimatedDeliveryTime },
                  { label: 'Delivery Radius', value: profile.deliveryRadius ? `${profile.deliveryRadius} miles` : null },
                  { label: 'Rating', value: profile.rating > 0 ? `⭐ ${profile.rating?.toFixed(1)} (${profile.totalRatings} reviews)` : 'No ratings yet' },
                ].filter(r => r.value).map(row => (
                  <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-gray-50">
                    <span className="text-gray-500">{row.label}</span>
                    <span className="font-medium text-gray-800">{row.value}</span>
                  </div>
                ))}
                {profile.description && (
                  <div className="pt-3">
                    <p className="text-xs text-gray-500 mb-1">Description</p>
                    <p className="text-sm text-gray-700">{profile.description}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* UK Food Safety Compliance */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-4">🇬🇧 UK Food Safety Compliance</h3>
              <div className="space-y-1 text-sm">
                {[
                  { label: 'Food Business Reg. No.', value: profile.compliance?.foodBusinessRegistrationNumber || 'Not provided' },
                  { label: 'Local Authority', value: profile.compliance?.registeredLocalAuthority || 'Not provided' },
                  { label: 'FHRS Rating', value: profile.compliance?.fhrsRating != null ? `${profile.compliance.fhrsRating} / 5` : 'Not rated' },
                  { label: 'Hygiene Certificate', value: profile.compliance?.foodHygieneCertificateLevel || 'Not provided' },
                  { label: 'Cert Expiry', value: profile.compliance?.foodHygieneCertificateExpiry || 'N/A' },
                  { label: 'Allergen Policy', value: profile.compliance?.allergenPolicyAcknowledged ? '✅ Acknowledged' : '❌ Not acknowledged' },
                  { label: 'Allergen Menu', value: profile.compliance?.allergenMenuAvailable ? '✅ Available' : '⚠️ Not provided' },
                  { label: 'VAT Number', value: profile.vatNumber || 'Not provided' },
                ].map(row => (
                  <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-gray-50">
                    <span className="text-gray-500 text-xs">{row.label}</span>
                    <span className="font-medium text-gray-800 text-xs">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700">
                  📋 To update compliance documents or FHRS rating, contact <strong>compliance@brickandcapital.co.uk</strong>.
                  Annual re-verification is required under UK food safety law.
                </p>
              </div>
            </div>

            {/* Platform Fees */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-3">Platform Fees</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Restaurant commission</span>
                  <span className="font-bold text-sm">
                    {stats.fulfillmentMode === 'self_delivery' ? '14%' : '25%'}
                    <span className="text-xs text-gray-400 ml-1 font-normal">
                      ({stats.fulfillmentMode === 'self_delivery' ? 'Self-Delivery' : 'Platform'})
                    </span>
                  </span>
                </div>
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Guest service fee</span>
                  <span className="font-bold text-sm">
                    {stats.fulfillmentMode === 'self_delivery' ? '£0.50 flat' : '£0.85 (£0.50 + £0.35 insurance)'}
                  </span>
                </div>
                {stats.fulfillmentMode === 'platform_delivery' && (
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Delivery rider commission</span>
                    <span className="font-bold text-sm">8% of delivery fee</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Payouts are processed weekly. For payment queries contact hello@ariv.co.uk
              </p>
            </div>
          </div>
        </div>

          {/* ── Full-width sections below the two-column grid ── */}

          {/* Operating Hours */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">🕐 Operating Schedule</h3>
              {!scheduleEditing ? (
                <button
                  onClick={handleScheduleEdit}
                  className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                >
                  Edit Schedule
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setScheduleEditing(false)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleScheduleSave}
                    disabled={scheduleSaving}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {scheduleSaving ? 'Saving...' : 'Save Schedule'}
                  </button>
                </div>
              )}
            </div>

            {!scheduleEditing ? (
              /* ── Read-only view ── */
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => {
                  const hours = profile.operatingHours?.[day.toLowerCase()];
                  return (
                    <div key={day} className={`rounded-lg p-3 text-center text-sm ${hours?.open ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <p className="font-semibold text-gray-800">{day.slice(0,3)}</p>
                      <p className={`text-xs mt-1 ${hours?.open ? 'text-green-700' : 'text-gray-400'}`}>
                        {hours?.open ? `${hours.openTime || '11:00'} - ${hours.closeTime || '23:00'}` : 'Closed'}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── Editing view ── */
              <div className="space-y-3">
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => {
                  const dayKey = day.toLowerCase();
                  const dayData = scheduleForm[dayKey] || { open: false, openTime: '11:00', closeTime: '23:00' };
                  return (
                    <div
                      key={day}
                      className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border transition-colors ${dayData.open ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                    >
                      {/* Day name */}
                      <span className="font-semibold text-sm text-gray-800 w-24 shrink-0">{day}</span>

                      {/* Toggle switch */}
                      <button
                        type="button"
                        onClick={() => {
                          setScheduleForm(prev => ({
                            ...prev,
                            [dayKey]: { ...prev[dayKey], open: !prev[dayKey].open }
                          }));
                        }}
                        className={`w-10 h-6 rounded-full flex items-center px-0.5 cursor-pointer transition-colors shrink-0 ${dayData.open ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'}`}
                      >
                        <div className="w-5 h-5 bg-white rounded-full shadow transition-all" />
                      </button>

                      <span className={`text-xs font-medium w-12 shrink-0 ${dayData.open ? 'text-green-700' : 'text-gray-400'}`}>
                        {dayData.open ? 'Open' : 'Closed'}
                      </span>

                      {/* Time pickers */}
                      {dayData.open && (
                        <div className="flex items-center gap-2 flex-1">
                          <select
                            value={dayData.openTime}
                            onChange={(e) => {
                              setScheduleForm(prev => ({
                                ...prev,
                                [dayKey]: { ...prev[dayKey], openTime: e.target.value }
                              }));
                            }}
                            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                          >
                            {timeOptions.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <span className="text-gray-400 text-sm">to</span>
                          <select
                            value={dayData.closeTime}
                            onChange={(e) => {
                              setScheduleForm(prev => ({
                                ...prev,
                                [dayKey]: { ...prev[dayKey], closeTime: e.target.value }
                              }));
                            }}
                            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                          >
                            {timeOptions.map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-gray-400 mt-3">
              {scheduleEditing
                ? 'Toggle each day on or off and set your opening and closing times.'
                : 'Click "Edit Schedule" to update your operating hours.'}
            </p>
          </div>

          {/* Order Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">📋 Order Settings</h3>
              {!orderSettingsEditing ? (
                <button onClick={handleOrderSettingsEdit} className="text-sm text-[#1F3A68] hover:underline font-medium">Edit</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setOrderSettingsEditing(false)} className="text-sm text-gray-500 hover:underline">Cancel</button>
                  <button
                    onClick={handleOrderSettingsSave}
                    disabled={orderSettingsSaving}
                    className="px-3 py-1 bg-[#1F3A68] text-white rounded-lg text-sm font-medium hover:bg-[#162d52] disabled:opacity-50"
                  >
                    {orderSettingsSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {orderSettingsEditing ? (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Minimum Order (£)</label>
                  <input
                    type="number"
                    step="0.50"
                    min="0"
                    value={orderSettingsForm.minimumOrderValue}
                    onChange={e => setOrderSettingsForm(prev => ({ ...prev, minimumOrderValue: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                    placeholder="e.g. 10.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Orders below this value won't be accepted</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Estimated Delivery Time</label>
                  <select
                    value={orderSettingsForm.estimatedDeliveryTime}
                    onChange={e => setOrderSettingsForm(prev => ({ ...prev, estimatedDeliveryTime: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                  >
                    <option value="15-25 mins">15-25 mins</option>
                    <option value="20-30 mins">20-30 mins</option>
                    <option value="25-35 mins">25-35 mins</option>
                    <option value="30-45 mins">30-45 mins</option>
                    <option value="35-50 mins">35-50 mins</option>
                    <option value="40-55 mins">40-55 mins</option>
                    <option value="45-60 mins">45-60 mins</option>
                    <option value="60-75 mins">60-75 mins</option>
                    <option value="75-90 mins">75-90 mins</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Shown to customers before ordering</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Delivery Radius (miles)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="30"
                    value={orderSettingsForm.deliveryRadius}
                    onChange={e => setOrderSettingsForm(prev => ({ ...prev, deliveryRadius: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                    placeholder="e.g. 5"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum distance for delivery orders</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">Default Prep Time (minutes)</label>
                  <input
                    type="number"
                    step="5"
                    min="5"
                    max="120"
                    value={orderSettingsForm.defaultPrepTime}
                    onChange={e => setOrderSettingsForm(prev => ({ ...prev, defaultPrepTime: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                    placeholder="e.g. 20"
                  />
                  <p className="text-xs text-gray-500 mt-1">Shown to customers on your restaurant page. Per-item prep time overrides this.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Order Types</p>
                  <div className="flex flex-col gap-2 mt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={orderSettingsForm.acceptsDelivery}
                        onChange={e => setOrderSettingsForm(prev => ({ ...prev, acceptsDelivery: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-[#1F3A68] focus:ring-[#1F3A68]"
                      />
                      <span className="text-sm font-medium text-gray-700">🛵 Delivery</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={orderSettingsForm.acceptsCollection}
                        onChange={e => setOrderSettingsForm(prev => ({ ...prev, acceptsCollection: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-[#1F3A68] focus:ring-[#1F3A68]"
                      />
                      <span className="text-sm font-medium text-gray-700">🏃 Collection</span>
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Minimum Order</p>
                  <p className="text-2xl font-bold text-gray-900">£{profile.minimumOrderValue || '0.00'}</p>
                  <p className="text-xs text-gray-500 mt-1">Orders below this value won't be accepted</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Estimated Delivery</p>
                  <p className="text-2xl font-bold text-gray-900">{profile.estimatedDeliveryTime || '30-45 mins'}</p>
                  <p className="text-xs text-gray-500 mt-1">Shown to customers before ordering</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Delivery Radius</p>
                  <p className="text-2xl font-bold text-gray-900">{profile.deliveryRadius || '5'} miles</p>
                  <p className="text-xs text-gray-500 mt-1">Maximum distance for delivery orders</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Default Prep Time</p>
                  <p className="text-2xl font-bold text-gray-900">{profile.defaultPrepTime ? `${profile.defaultPrepTime} min` : 'Not set'}</p>
                  <p className="text-xs text-gray-500 mt-1">Shown on your listing. Per-item times override this.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Order Types</p>
                  <div className="flex gap-2 mt-1">
                    {profile.acceptsDelivery !== false && <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">🛵 Delivery</span>}
                    {profile.acceptsCollection !== false && <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">🏃 Collection</span>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bank & Payout Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">🏦 Payout Details</h3>
              {!payoutEditing ? (
                <button onClick={handlePayoutEdit} className="text-sm text-[#1F3A68] hover:underline font-medium">Edit</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setPayoutEditing(false)} className="text-sm text-gray-500 hover:underline">Cancel</button>
                  <button
                    onClick={handlePayoutSave}
                    disabled={payoutSaving}
                    className="px-3 py-1 bg-[#1F3A68] text-white rounded-lg text-sm font-medium hover:bg-[#162d52] disabled:opacity-50"
                  >
                    {payoutSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {payoutEditing ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Account Name</label>
                    <input
                      type="text"
                      value={payoutForm.bankAccountName}
                      onChange={e => setPayoutForm(prev => ({ ...prev, bankAccountName: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                      placeholder="e.g. Bella Italia Ltd"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Sort Code</label>
                    <input
                      type="text"
                      value={payoutForm.bankSortCode}
                      onChange={e => {
                        // Auto-format sort code: 12-34-56
                        let val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                        if (val.length > 4) val = val.slice(0, 2) + '-' + val.slice(2, 4) + '-' + val.slice(4);
                        else if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
                        setPayoutForm(prev => ({ ...prev, bankSortCode: val }));
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                      placeholder={profile?.bankDetails?.sortCode ? 'Leave blank to keep current' : 'e.g. 12-34-56'}
                      maxLength={8}
                      autoComplete="off"
                    />
                    {profile?.bankDetails?.sortCode && (
                      <p className="text-xs text-gray-400 mt-1">
                        On file: ••-••-{String(profile.bankDetails.sortCode).slice(-2)}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Account Number</label>
                    <input
                      type="text"
                      value={payoutForm.bankAccountNumber}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                        setPayoutForm(prev => ({ ...prev, bankAccountNumber: val }));
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                      placeholder={profile?.bankDetails?.accountNumber ? 'Leave blank to keep current' : 'e.g. 12345678'}
                      maxLength={8}
                      autoComplete="off"
                    />
                    {profile?.bankDetails?.accountNumber && (
                      <p className="text-xs text-gray-400 mt-1">
                        On file: ••••{String(profile.bankDetails.accountNumber).slice(-4)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <span className="text-amber-500 text-lg">⚠️</span>
                  <div>
                    <p className="text-xs text-amber-800 font-semibold">Please double-check your bank details</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Incorrect details may delay your weekly payouts. Changes take effect from the next payout cycle.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Account Name</p>
                      <p className="text-sm font-medium text-gray-800">{profile.bankDetails?.accountName || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Sort Code</p>
                      <p className="text-sm font-medium text-gray-800">
                        {profile.bankDetails?.sortCode
                          ? '••-••-' + (profile.bankDetails.sortCode.slice(-2) || '••')
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Account Number</p>
                      <p className="text-sm font-medium text-gray-800">
                        {profile.bankDetails?.accountNumber
                          ? '••••' + (profile.bankDetails.accountNumber.slice(-4) || '••••')
                          : '—'}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-3">💳 Payouts are processed weekly (Wednesdays). Click <strong>Edit</strong> to update your bank details.</p>
              </>
            )}
          </div>

          {/* Notifications Preferences */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
            <h3 className="font-semibold text-gray-800 mb-4">🔔 Notification Preferences</h3>
            {notifPrefs ? (
              <div className="space-y-3">
                {[
                  { label: 'New Order Alerts', desc: 'Sound + email when new order is placed', icon: '📦', key: 'newOrderAlerts' },
                  { label: 'Order Status Updates', desc: 'Email when rider picks up / delivers', icon: '🛵', key: 'orderStatusUpdates' },
                  { label: 'Daily Sales Summary', desc: 'Email summary of daily orders & revenue', icon: '📊', key: 'dailySalesSummary' },
                  { label: 'Customer Reviews', desc: 'Notify when a customer leaves a review', icon: '⭐', key: 'customerReviews' },
                  { label: 'Low Stock Alerts', desc: 'Warn when menu items have many orders', icon: '⚠️', key: 'lowStockAlerts' },
                ].map((pref, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{pref.icon}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{pref.label}</p>
                        <p className="text-xs text-gray-500">{pref.desc}</p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        const updated = { ...notifPrefs, [pref.key]: !notifPrefs[pref.key] };
                        setNotifPrefs(updated);
                        // Persist to backend
                        try {
                          const saved = await restaurantAPI.updateNotificationPreferences(updated, token);
                          // Sync from backend response to ensure consistency
                          setNotifPrefs(saved);
                          setSuccess(`${pref.label} ${saved[pref.key] ? 'enabled' : 'disabled'}`);
                          setTimeout(() => setSuccess(''), 2500);
                        } catch (e) {
                          console.error('Failed to save notification preference:', e);
                          // Revert on failure
                          setNotifPrefs(prev => ({ ...prev, [pref.key]: !updated[pref.key] }));
                          setError('Failed to save preference');
                          setTimeout(() => setError(''), 3000);
                        }
                      }}
                      className={`w-10 h-6 rounded-full flex items-center px-0.5 cursor-pointer transition-colors ${notifPrefs[pref.key] ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'}`}
                    >
                      <div className="w-5 h-5 bg-white rounded-full shadow transition-all" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Loading preferences...</p>
            )}
            <p className="text-xs text-gray-400 mt-3">Changes are saved automatically. Email preferences apply to your restaurant account.</p>
          </div>

          {/* ── Fulfillment Mode & Hybrid Delivery Schedule ── */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
            <h3 className="font-semibold text-gray-800 mb-4">🚗 Delivery Fulfillment Mode</h3>

            {/* Toggle between self-delivery and platform delivery */}
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={async () => {
                  const newMode = stats.fulfillmentMode === 'self_delivery' ? 'platform_delivery' : 'self_delivery';
                  try {
                    await restaurantAPI.updateProfile({ fulfillmentMode: newMode }, token);
                    setSuccess(`Switched to ${newMode === 'self_delivery' ? 'Self-Delivery (14%)' : 'Platform Delivery (25%)'}`);
                    setTimeout(() => setSuccess(''), 3000);
                    loadDashboard();
                  } catch (e) { setError('Failed to update fulfillment mode'); setTimeout(() => setError(''), 3000); }
                }}
                className={`flex-1 p-4 rounded-xl border-2 text-center transition-all cursor-pointer ${
                  stats.fulfillmentMode === 'self_delivery'
                    ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-green-300'
                }`}
              >
                <span className="text-2xl block mb-1">🏪</span>
                <p className="font-bold text-sm">Self-Delivery</p>
                <p className="text-xs text-gray-500">12% commission</p>
              </button>
              <button
                onClick={async () => {
                  const newMode = stats.fulfillmentMode === 'platform_delivery' ? 'self_delivery' : 'platform_delivery';
                  try {
                    await restaurantAPI.updateProfile({ fulfillmentMode: newMode }, token);
                    setSuccess(`Switched to ${newMode === 'self_delivery' ? 'Self-Delivery (14%)' : 'Platform Delivery (25%)'}`);
                    setTimeout(() => setSuccess(''), 3000);
                    loadDashboard();
                  } catch (e) { setError('Failed to update fulfillment mode'); setTimeout(() => setError(''), 3000); }
                }}
                className={`flex-1 p-4 rounded-xl border-2 text-center transition-all cursor-pointer ${
                  stats.fulfillmentMode === 'platform_delivery'
                    ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                }`}
              >
                <span className="text-2xl block mb-1">🛵</span>
                <p className="font-bold text-sm">Platform Delivery</p>
                <p className="text-xs text-gray-500">22% commission</p>
              </button>
            </div>

            {/* Weekly Schedule */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700">📅 Weekly Delivery Schedule</p>
                {!deliveryScheduleEditing ? (
                  <button
                    onClick={() => {
                      setDeliveryScheduleEditing(true);
                      // Initialize form from saved schedule
                      const saved = deliveryScheduleData?.schedule || [];
                      const form = {};
                      ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].forEach((d, i) => {
                        const entry = saved.find(s => s.dayOfWeek === i);
                        form[i] = entry?.mode || stats.fulfillmentMode || 'platform_delivery';
                      });
                      setDeliveryScheduleForm(form);
                    }}
                    className="text-xs text-[#1F3A68] font-medium hover:underline"
                  >
                    Edit Schedule
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => setDeliveryScheduleEditing(false)} className="text-xs text-gray-500 hover:underline">Cancel</button>
                    <button
                      onClick={async () => {
                        try {
                          const schedule = Object.entries(deliveryScheduleForm).map(([day, mode]) => ({
                            dayOfWeek: parseInt(day), mode
                          }));
                          await restaurantAPI.updateDeliverySchedule(schedule, token);
                          setDeliveryScheduleEditing(false);
                          setSuccess('Delivery schedule saved!');
                          setTimeout(() => setSuccess(''), 3000);
                          // Reload
                          const data = await restaurantAPI.getDeliverySchedule(token);
                          setDeliveryScheduleData(data);
                        } catch (e) { setError('Failed to save schedule'); setTimeout(() => setError(''), 3000); }
                      }}
                      className="text-xs text-white bg-[#1F3A68] px-3 py-1 rounded-lg font-medium hover:bg-[#162d52]"
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>

              {deliveryScheduleEditing ? (
                <div className="space-y-2">
                  {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((day, i) => (
                    <div key={day} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700 w-20">{day.slice(0,3)}</span>
                      <select
                        value={deliveryScheduleForm[i] || 'platform_delivery'}
                        onChange={e => setDeliveryScheduleForm(prev => ({...prev, [i]: e.target.value}))}
                        className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
                      >
                        <option value="self_delivery">🏪 Self-Delivery (14%)</option>
                        <option value="platform_delivery">🛵 Platform (25%)</option>
                        <option value="collection_only">🛍️ Collection Only (14%)</option>
                      </select>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-7 gap-2">
                  {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((day, i) => {
                    const saved = deliveryScheduleData?.schedule || [];
                    const entry = saved.find(s => s.dayOfWeek === i);
                    const mode = entry?.mode || stats.fulfillmentMode || 'platform_delivery';
                    const isCollection = mode === 'collection_only';
                    const isSelf = mode === 'self_delivery';
                    const colorClass = isCollection ? 'bg-amber-50 border border-amber-200' : isSelf ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200';
                    const textClass = isCollection ? 'text-amber-700' : isSelf ? 'text-green-700' : 'text-blue-700';
                    const icon = isCollection ? '🛍️' : isSelf ? '🏪' : '🛵';
                    return (
                      <div key={day} className={`rounded-lg p-2 text-center text-xs ${colorClass}`}>
                        <p className="font-semibold text-gray-800">{day}</p>
                        <p className={`mt-1 ${textClass}`}>
                          {icon}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">
                Set different delivery modes per day. E.g., own drivers on weekdays, platform riders on weekends, collection only on Sundays.
              </p>
            </div>
          </div>

          {/* ── Driver Management (Self-Delivery Only) ── */}
          {stats.fulfillmentMode === 'self_delivery' && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">👥 In-House Drivers</h3>
                <button
                  onClick={() => setShowAddDriverModal(true)}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                >
                  + Add Driver
                </button>
              </div>

              {staffDrivers.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <span className="text-3xl block mb-2">🚗</span>
                  <p className="text-sm text-gray-500">No drivers added yet</p>
                  <p className="text-xs text-gray-400 mt-1">Add your in-house delivery drivers to manage your fleet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {staffDrivers.map(driver => (
                    <div key={driver.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          driver.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {driver.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{driver.name}</p>
                          <p className="text-xs text-gray-500">{driver.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          driver.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {driver.status === 'active' ? '✅ Active' : '📩 Invited'}
                        </span>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Remove ${driver.name} from your delivery team?`)) return;
                            try {
                              await restaurantAPI.removeStaff(driver.id, token);
                              setStaffDrivers(prev => prev.filter(d => d.id !== driver.id));
                              setSuccess('Driver removed');
                              setTimeout(() => setSuccess(''), 3000);
                            } catch (e) { setError('Failed to remove driver'); setTimeout(() => setError(''), 3000); }
                          }}
                          className="text-xs text-red-500 hover:text-red-700 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-3">
                Your drivers will receive an invitation email. They must enter the 6-digit pickup code from the bag seal to confirm order collection.
              </p>
            </div>
          )}

          {/* Add Driver Modal */}
          {showAddDriverModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddDriverModal(false)}>
              <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Add In-House Driver</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Driver Name</label>
                    <input
                      type="text"
                      value={addDriverForm.name}
                      onChange={e => setAddDriverForm(prev => ({...prev, name: e.target.value}))}
                      placeholder="e.g. John Smith"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={addDriverForm.email}
                      onChange={e => setAddDriverForm(prev => ({...prev, email: e.target.value}))}
                      placeholder="driver@email.com"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button
                    onClick={() => setShowAddDriverModal(false)}
                    className="flex-1 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!addDriverForm.name.trim() || !addDriverForm.email.trim()) {
                        setError('Name and email are required'); setTimeout(() => setError(''), 3000); return;
                      }
                      try {
                        const result = await restaurantAPI.addStaff(addDriverForm, token);
                        setShowAddDriverModal(false);
                        setAddDriverForm({ name: '', email: '' });
                        setSuccess(result.message || 'Driver added!');
                        setTimeout(() => setSuccess(''), 3000);
                        // Reload staff list
                        const data = await restaurantAPI.getStaff(token);
                        setStaffDrivers(data.staff || []);
                      } catch (e) {
                        setError(e.response?.data?.detail || 'Failed to add driver');
                        setTimeout(() => setError(''), 3000);
                      }
                    }}
                    className="flex-1 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 font-medium transition-colors"
                  >
                    Send Invitation
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pickup Code Modal (Self-Delivery) */}
          {showPickupCodeModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPickupCodeModal(false)}>
              <div className="bg-white rounded-xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-gray-900 mb-2">🔐 Enter Pickup Code</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your driver must enter the 6-character code from the order bag seal to confirm collection.
                </p>
                <input
                  type="text"
                  maxLength={6}
                  value={pickupCodeInput}
                  onChange={e => setPickupCodeInput(e.target.value.toUpperCase())}
                  placeholder="e.g. A9X21B"
                  className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 text-2xl font-mono text-center tracking-[0.3em] focus:outline-none focus:border-[#1F3A68] focus:ring-2 focus:ring-[#1F3A68]/20"
                  autoFocus
                />
                {pickupCodeError && (
                  <p className="text-sm text-red-600 mt-2 text-center">{pickupCodeError}</p>
                )}
                <div className="flex gap-2 mt-5">
                  <button onClick={() => { setShowPickupCodeModal(false); setPickupCodeError(''); }} className="flex-1 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                  <button
                    onClick={async () => {
                      if (pickupCodeInput.length < 4) {
                        setPickupCodeError('Enter the full code from the bag seal');
                        return;
                      }
                      try {
                        await restaurantAPI.updateOrderStatus(pickupCodeOrderId, {
                          status: 'out_for_delivery',
                          pickupCode: pickupCodeInput
                        }, token);
                        setShowPickupCodeModal(false);
                        setPickupCodeInput('');
                        setPickupCodeError('');
                        setSuccess('Order marked out for delivery!');
                        setTimeout(() => setSuccess(''), 3000);
                        loadOrders();
                      } catch (e) {
                        setPickupCodeError(e.response?.data?.detail || 'Invalid pickup code');
                      }
                    }}
                    disabled={pickupCodeInput.length < 4}
                    className="flex-1 py-2 text-sm text-white bg-[#1F3A68] rounded-lg hover:bg-[#162d52] font-medium transition-colors disabled:opacity-50"
                  >
                    Verify & Send
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Help & Support */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
            <h3 className="font-semibold text-gray-800 mb-4">💬 Help & Support</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <button
                onClick={() => setShowGuideModal(true)}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer text-left"
              >
                <span className="text-2xl mb-2 block">📖</span>
                <p className="font-semibold text-sm text-gray-800">Restaurant Guide</p>
                <p className="text-xs text-gray-500 mt-1">Tips for managing orders, menu & ratings</p>
              </button>
              <button
                onClick={() => {
                  window.location.href = 'mailto:hello@ariv.co.uk?subject=Restaurant Partner Support Request';
                }}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer text-left"
              >
                <span className="text-2xl mb-2 block">📞</span>
                <p className="font-semibold text-sm text-gray-800">Contact Support</p>
                <p className="text-xs text-gray-500 mt-1">hello@ariv.co.uk</p>
              </button>
              <button
                onClick={() => {
                  window.open('/terms', '_blank');
                }}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer text-left"
              >
                <span className="text-2xl mb-2 block">📋</span>
                <p className="font-semibold text-sm text-gray-800">Terms & Conditions</p>
                <p className="text-xs text-gray-500 mt-1">Restaurant partner agreement</p>
              </button>
            </div>
          </div>

          {/* Restaurant Guide Modal */}
          {showGuideModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-[#1F3A68]">📖 Restaurant Partner Guide</h3>
                  <button onClick={() => setShowGuideModal(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
                </div>
                <div className="space-y-4 text-sm text-gray-700">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">📋 Managing Orders</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600">
                      <li>New orders appear in the Orders tab with a sound alert</li>
                      <li>Accept orders promptly — set a prep time so customers know when to expect delivery</li>
                      <li>Mark orders as "Ready" to trigger rider assignment automatically</li>
                      <li>Reject orders with a reason — this is shown to the customer</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">🍽 Menu Management</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600">
                      <li>Add photos — items with photos get 3x more orders</li>
                      <li>Keep allergen info up to date (UK law requires this)</li>
                      <li>Toggle item availability instantly if you run out of stock</li>
                      <li>Use categories to keep your menu organised</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">⭐ Improving Ratings</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600">
                      <li>Respond to customer reviews — restaurants that respond get 15% more repeat orders</li>
                      <li>Accurate prep times build trust and reduce cancellations</li>
                      <li>Use promo codes to encourage first-time orders</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">💰 Payouts</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600">
                      <li>Payouts are processed every Wednesday</li>
                      <li>Platform commission: 14% (self-delivery) / 25% (platform delivery)</li>
                      <li>Check your Analytics tab for detailed revenue breakdowns</li>
                    </ul>
                  </div>
                </div>
                <button
                  onClick={() => setShowGuideModal(false)}
                  className="mt-6 w-full py-2.5 bg-[#1F3A68] text-white rounded-lg font-medium hover:bg-[#2d5299] transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          )}

          {/* Account Actions */}
          <div className="bg-white rounded-xl border border-red-200 p-5 mt-6">
            <h3 className="font-semibold text-red-700 mb-3">⚠️ Account Actions</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => { setSnoozeMinutes(null); setShowBusyConfirm(true); }}
                className={`px-4 py-2 text-sm border rounded-lg transition-colors ${
                  isBusy
                    ? 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                    : 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'
                }`}
              >
                {isBusy ? '🟢 Resume Orders' : '🔴 Go Busy / Snooze'}
              </button>
              {isBusy && busyUntil && (
                <span className="text-xs text-amber-600 font-medium self-center">
                  ⏰ Auto-resumes {new Date(busyUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <button
                onClick={() => setShowClosureConfirm(true)}
                className="px-4 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
              >
                Request Account Closure
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3">Busy mode / snooze shows a &apos;Currently Busy&apos; badge to customers and stops new orders. Snooze auto-resumes after the set time. Existing orders continue. Account closure requires 30 days notice per our terms.</p>
          </div>

          {/* Busy Mode / Snooze Confirmation Modal */}
          {showBusyConfirm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl max-w-md w-full p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {isBusy ? '🟢 Resume Orders?' : '🔴 Snooze / Go Busy'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {isBusy
                    ? 'Your restaurant will start accepting new orders again. Customers will see you as available.'
                    : 'Your restaurant will show a "Currently Busy" badge. Choose a snooze duration to auto-resume, or go indefinitely busy until you manually resume.'
                  }
                </p>

                {/* Snooze duration picker — only when activating */}
                {!isBusy && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Snooze Duration</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: '15 min', value: 15 },
                        { label: '30 min', value: 30 },
                        { label: '1 hour', value: 60 },
                        { label: '2 hours', value: 120 },
                        { label: '4 hours', value: 240 },
                        { label: 'Indefinite', value: null },
                      ].map(opt => (
                        <button
                          key={opt.label}
                          onClick={() => setSnoozeMinutes(opt.value)}
                          className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                            snoozeMinutes === opt.value
                              ? 'bg-amber-100 border-amber-400 text-amber-800'
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {snoozeMinutes && (
                      <p className="text-xs text-amber-600 mt-2">
                        Orders will auto-resume at {new Date(Date.now() + snoozeMinutes * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowBusyConfirm(false)}
                    className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const result = await restaurantAPI.toggleBusyMode(token, isBusy ? null : snoozeMinutes);
                        setIsBusy(result.isBusy);
                        setBusyUntil(result.busyUntil || null);
                        setShowBusyConfirm(false);
                        setSuccess(result.message);
                        setTimeout(() => setSuccess(''), 4000);
                      } catch (e) {
                        console.error('Failed to toggle busy mode:', e);
                        setShowBusyConfirm(false);
                        setSuccess('');
                      }
                    }}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-bold text-white transition-colors ${
                      isBusy ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'
                    }`}
                  >
                    {isBusy ? 'Resume Now' : snoozeMinutes ? `Snooze ${snoozeMinutes} min` : 'Go Busy'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Account Closure Confirmation Modal */}
          {showClosureConfirm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl max-w-md w-full p-6">
                <h3 className="text-lg font-bold text-red-700 mb-2">Request Account Closure</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Account closure requires 30 days notice as per our restaurant partner agreement. During this period:
                </p>
                <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1 mb-4">
                  <li>You should fulfil any outstanding orders</li>
                  <li>Final payouts will be processed</li>
                  <li>Your menu and reviews will be removed</li>
                  <li>This action cannot be undone</li>
                </ul>
                <p className="text-sm text-gray-600 mb-4">
                  To proceed, please email <strong>hello@ariv.co.uk</strong> with the subject "Account Closure Request" and your restaurant name.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowClosureConfirm(false)}
                    className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      window.location.href = `mailto:hello@ariv.co.uk?subject=Account Closure Request — ${profile?.restaurantName || 'Restaurant'}&body=Hi,%0A%0AI would like to request closure of my restaurant account.%0A%0ARestaurant Name: ${profile?.restaurantName || ''}%0AEmail: ${user?.email || ''}%0A%0APlease process my 30-day notice period.%0A%0AThank you.`;
                      setShowClosureConfirm(false);
                    }}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold transition-colors"
                  >
                    Email Closure Request
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════ FAQ TAB ══════════════ */}
      {tab === 'faq' && (
        <FAQFeedback />
      )}
    </div>
  );

  // ─── ORDER CARD COMPONENT ──────────────────────────────────────────────────
  function renderOrderCard(order, isNew) {
    const isSelfDeliveryMode = stats.fulfillmentMode === 'self_delivery';
    const flowMap = isSelfDeliveryMode ? SELF_DELIVERY_STATUS_FLOW : ORDER_STATUS_FLOW;
    const statusInfo = flowMap[order.status];
    const isLoading = actionLoading[order._id];
    const elapsed = timeSince(order.createdAt);

    return (
      <div key={order._id} className={`bg-white rounded-xl border-2 p-5 transition-all ${
        isNew ? 'border-red-400 shadow-lg shadow-red-100' : statusInfo?.ring ? `border-transparent ring-1 ${statusInfo.ring}` : 'border-gray-200'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-bold text-gray-900 text-lg">#{order.orderNumber}</h4>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusInfo?.color}`}>
                {statusInfo?.icon} {order.status?.replace(/_/g, ' ')}
              </span>
              {isNew && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white animate-pulse">NEW</span>}
            </div>
            <p className="text-sm text-gray-600 mt-0.5 flex items-center gap-2">
              <span>👤 {order.guestName || 'Guest'}</span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-400">🕐 {elapsed}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="font-bold text-gray-900 text-xl">£{order.total?.toFixed(2)}</p>
            <p className="text-xs text-gray-400 mt-0.5">{order.items?.length} item{order.items?.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {/* Order items */}
        <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
          {order.items?.map((item, i) => (
            <div key={i} className="flex justify-between text-gray-700 py-0.5">
              <span>{item.quantity}× {item.name}{item.allergenNotes ? ` (⚠ ${item.allergenNotes})` : ''}</span>
              <span className="font-medium">£{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between font-bold text-gray-800 pt-2 border-t border-gray-200 mt-1">
            <span>Total</span><span>£{order.total?.toFixed(2)}</span>
          </div>
        </div>

        {/* Delivery info */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-4 flex-wrap">
          {order.deliveryType === 'collection' ? (
            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md font-medium">🏃 Collection</span>
          ) : (
            <span className="px-2 py-1 bg-green-50 text-green-700 rounded-md font-medium">📍 {order.deliveryAddress}</span>
          )}
          {order.notes && (
            <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-md">📝 {order.notes}</span>
          )}
        </div>

        {/* 6-Digit Pickup Code — always visible, printed on bag seal receipt */}
        {order.pickupCode && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">Pickup Code</p>
                <p className="text-xs text-amber-600 mt-0.5">Printed on bag seal — rider enters this to unlock delivery address</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-black text-amber-900 tracking-[0.25em] font-mono bg-white px-3 py-1 rounded-lg border border-amber-200">
                  {order.pickupCode?.slice(0,3)}-{order.pickupCode?.slice(3)}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); printOrderReceipt(order); }}
                  className="p-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                  title="Print receipt with pickup code"
                >
                  🖨️
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pickup PIN (shown after order is accepted) */}
        {order.pickupPin && ['accepted', 'preparing', 'ready_for_pickup'].includes(order.status) && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-indigo-600 uppercase">Delivery PIN</p>
              <p className="text-xs text-indigo-500 mt-0.5">Guest's PIN — rider enters at delivery for proof</p>
            </div>
            <span className="text-2xl font-bold text-indigo-700 tracking-widest font-mono">{order.pickupPin}</span>
          </div>
        )}

        {/* Problem reported — contest button for restaurant */}
        {order.problemReported && order.refundStatus === 'requested' && !order.disputeStatus && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-red-600 uppercase">Refund Claimed</p>
                <p className="text-sm text-red-700 font-medium">{order.problemCategory} — £{order.refundAmount?.toFixed(2)}</p>
                <p className="text-xs text-red-500 mt-0.5">{order.problemDescription || 'No details provided'}</p>
              </div>
              <button
                onClick={async () => {
                  const reason = prompt('Why are you contesting this refund? (e.g. CCTV confirms item was included)');
                  if (!reason) return;
                  try {
                    await restaurantAPI.contestRefund(order._id, reason, '', token);
                    setSuccess('Refund contested — admin will review');
                    setTimeout(() => setSuccess(''), 4000);
                    loadOrders();
                  } catch (e) { alert(e.response?.data?.detail || 'Failed to contest'); }
                }}
                className="px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700"
              >
                Contest Refund
              </button>
            </div>
          </div>
        )}
        {order.disputeStatus === 'contested' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3 text-center">
            <p className="text-xs font-medium text-amber-700">⚖️ Refund contested — awaiting admin decision</p>
          </div>
        )}

        {/* Rider fault — order not delivered + ban option */}
        {order.problemType === 'order_not_delivered' && order.faultRiderName && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-3 mb-3">
            <p className="text-xs font-bold text-red-700 uppercase mb-1">Rider Fault — Food Not Delivered</p>
            <div className="text-sm text-red-800 space-y-0.5">
              <p>Rider: <strong>{order.faultRiderName}</strong></p>
              {order.faultRiderPhone && <p>Phone: <strong>{order.faultRiderPhone}</strong></p>}
              {order.faultRiderMethod && <p>Method: <strong>{order.faultRiderMethod}</strong></p>}
            </div>
            <p className="text-xs text-red-600 mt-2">Rider's pay has been forfeited. Their reliability score has been reduced.</p>
            {!order.riderBlacklisted ? (
              <button
                onClick={async () => {
                  if (!window.confirm(`Ban ${order.faultRiderName} from receiving delivery notifications from your restaurant?`)) return;
                  try {
                    await restaurantAPI.blacklistRider(order._id, token);
                    setSuccess(`${order.faultRiderName} has been banned from your deliveries`);
                    setTimeout(() => setSuccess(''), 4000);
                    loadOrders();
                  } catch (e) { alert(e.response?.data?.detail || 'Failed to ban rider'); }
                }}
                className="mt-2 w-full py-2 bg-red-700 text-white rounded-lg text-xs font-bold hover:bg-red-800 transition-colors"
              >
                🚫 Ban This Rider
              </button>
            ) : (
              <div className="mt-2 text-center py-2 bg-red-100 rounded-lg">
                <p className="text-xs font-bold text-red-700">✓ Rider Banned</p>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2 flex-wrap">
          {statusInfo?.next && (
            <button
              onClick={() => {
                if (order.status === 'placed') { setShowAcceptModal(order._id); return; }
                // Self-delivery: require pickup code to advance to out_for_delivery
                if (isSelfDeliveryMode && statusInfo.next === 'out_for_delivery') {
                  setPickupCodeOrderId(order._id);
                  setPickupCodeInput('');
                  setPickupCodeError('');
                  setShowPickupCodeModal(true);
                  return;
                }
                handleOrderAction(order._id, statusInfo.next);
              }}
              disabled={isLoading}
              className={`flex-1 py-3 rounded-lg font-bold text-sm text-white transition-colors disabled:opacity-50 min-w-0 ${
                statusInfo.actionColor || 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isLoading ? '⏳ Updating…' : `${statusInfo.icon} ${statusInfo.label}`}
            </button>
          )}
          {['ready_for_pickup', 'out_for_delivery'].includes(order.status) && !isSelfDeliveryMode && (
            <div className={`flex-1 text-center text-sm rounded-lg py-3 font-medium ${
              order.deliveryStatus === 'rider_arriving'
                ? 'text-amber-800 bg-amber-100 border border-amber-300 animate-pulse'
                : 'text-purple-700 bg-purple-50'
            }`}>
              {order.deliveryStatus === 'rider_arriving' && order.riderName ? (
                <>🔔 {order.riderName} is almost here — have the order ready!</>
              ) : order.riderName ? (
                <>
                  🛵 {order.riderName} — {order.status === 'out_for_delivery'
                    ? 'en route to customer'
                    : order.riderEta
                      ? `arriving in ${order.riderEta}`
                      : 'picking up soon'}
                </>
              ) : (
                <>🛵 Rider {order.status === 'out_for_delivery' ? 'en route to customer' : 'being assigned…'}</>
              )}
            </div>
          )}
          {REJECT_NEXT[order.status] && (
            <button
              onClick={() => setShowRejectModal(order._id)}
              disabled={isLoading}
              className="px-5 py-3 rounded-lg font-semibold text-sm text-red-600 border-2 border-red-200 hover:bg-red-50 disabled:opacity-50"
            >
              {order.status === 'placed' ? '❌ Reject' : '❌ Cancel'}
            </button>
          )}
          {/* No rider found — refund guest button (ready_for_pickup only, after 10+ min) */}
          {order.status === 'ready_for_pickup' && !order.riderName && (
            <button
              onClick={async () => {
                if (!window.confirm('No rider found. Refund the guest? (Food + delivery fee refunded, service fee retained)')) return;
                try {
                  setLoading(prev => ({ ...prev, [order._id]: true }));
                  await restaurantAPI.noRiderRefund(order._id, token);
                  setSuccess('Guest refunded. Order cancelled — no rider found.');
                  setTimeout(() => setSuccess(''), 5000);
                  loadOrders();
                } catch (e) {
                  alert(e.response?.data?.detail || 'Failed to process refund');
                } finally {
                  setLoading(prev => ({ ...prev, [order._id]: false }));
                }
              }}
              disabled={isLoading}
              className="flex-1 py-3 rounded-lg font-bold text-sm text-amber-700 border-2 border-amber-300 hover:bg-amber-50 disabled:opacity-50 min-w-0"
            >
              🚫 No Rider Found — Refund Guest
            </button>
          )}
        </div>
      </div>
    );
  }
}
